/**
 * app.js — tau-cam main application.
 *
 * Boot audio, run/stop, compile, event handling.
 * Combines boot.js + sudo.js from tau-ted, minus all sync/broadcast.
 */

import { InboxWriter } from './tau-state/inbox_ring.js';
import { SuperSonic } from './dist/supersonic.js';
import { compressToHash, decompressFromHash } from './share.js';
import Keyboard from 'simple-keyboard';

// --- Pre-build metrics panel from schema (shows structure before boot) ---
{
  const ssmPanel = document.getElementById('ssm-panel');
  if (ssmPanel && ssmPanel.buildFromSchema) ssmPanel.buildFromSchema(SuperSonic);
}

// --- Sample analysis (onset detection via spectral flux) ---
const sampleMeta = {};  // name → { duration, onsets: [normalized 0–1 positions] }
// UUID → stmtId mapping for correlating /n_end with piano roll bars
const _synthUuidToStmt = new Map();

function fftMagnitude(re, n) {
  const im = new Float32Array(n);
  // Bit-reversal permutation
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) { const t = re[i]; re[i] = re[j]; re[j] = t; }
  }
  // Cooley-Tukey
  for (let size = 2; size <= n; size *= 2) {
    const half = size >> 1;
    const step = -2 * Math.PI / size;
    for (let i = 0; i < n; i += size) {
      for (let k = 0; k < half; k++) {
        const ang = step * k;
        const cos = Math.cos(ang), sin = Math.sin(ang);
        const j = i + k + half;
        const tr = re[j] * cos - im[j] * sin;
        const ti = re[j] * sin + im[j] * cos;
        re[j] = re[i + k] - tr;  im[j] = im[i + k] - ti;
        re[i + k] += tr;          im[i + k] += ti;
      }
    }
  }
  const mag = new Float32Array(n >> 1);
  for (let i = 0; i < mag.length; i++) mag[i] = Math.sqrt(re[i] * re[i] + im[i] * im[i]);
  return mag;
}

function detectOnsets(pcm, sampleRate) {
  const frameSize = 1024, hop = 512;
  const numFrames = Math.floor((pcm.length - frameSize) / hop);
  if (numFrames < 2) return [0.0];

  // Hann window (precomputed)
  const win = new Float32Array(frameSize);
  for (let i = 0; i < frameSize; i++) win[i] = 0.5 * (1 - Math.cos(2 * Math.PI * i / (frameSize - 1)));

  // Compute spectral flux
  const flux = new Float32Array(numFrames);
  let prevMag = null;
  for (let f = 0; f < numFrames; f++) {
    const frame = new Float32Array(frameSize);
    const off = f * hop;
    for (let i = 0; i < frameSize; i++) frame[i] = pcm[off + i] * win[i];
    const mag = fftMagnitude(frame, frameSize);
    if (prevMag) {
      let sum = 0;
      for (let i = 0; i < mag.length; i++) {
        const d = mag[i] - prevMag[i];
        if (d > 0) sum += d;
      }
      flux[f] = sum;
    }
    prevMag = mag;
  }

  // Adaptive threshold (local median * multiplier) + minimum inter-onset gap
  const W = 10, mult = 2.5;
  const minGapFrames = Math.ceil(0.06 * sampleRate / hop); // 60ms minimum gap
  const onsets = [0.0]; // always include start
  const dur = pcm.length / sampleRate;
  let lastOnsetFrame = 0;
  for (let i = 1; i < numFrames; i++) {
    if (i - lastOnsetFrame < minGapFrames) continue;
    const lo = Math.max(0, i - W), hi = Math.min(numFrames, i + W + 1);
    const local = Array.from(flux.subarray(lo, hi)).sort((a, b) => a - b);
    const median = local[local.length >> 1];
    if (flux[i] > median * mult + 0.01 && flux[i] >= flux[i - 1] && (i + 1 >= numFrames || flux[i] >= flux[i + 1])) {
      const t = (i * hop) / sampleRate / dur; // normalized 0–1
      if (t > 0.01) {
        onsets.push(t);
        lastOnsetFrame = i;
      }
    }
  }
  return onsets;
}

function computeWaveform(pcm, numBins) {
  const binSize = Math.max(1, Math.floor(pcm.length / numBins));
  const mins = new Float32Array(numBins);
  const maxs = new Float32Array(numBins);
  for (let i = 0; i < numBins; i++) {
    let lo = 1, hi = -1;
    const start = i * binSize;
    const end = Math.min(start + binSize, pcm.length);
    for (let j = start; j < end; j++) {
      const v = pcm[j];
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    mins[i] = lo;
    maxs[i] = hi;
  }
  return { mins, maxs };
}

function computeSpectrum(pcm, sampleRate) {
  const frameSize = 2048, hop = 1024;
  const numFrames = Math.floor((pcm.length - frameSize) / hop);
  if (numFrames < 1) return null;

  const numBins = frameSize >> 1;
  const win = new Float32Array(frameSize);
  for (let i = 0; i < frameSize; i++) win[i] = 0.5 * (1 - Math.cos(2 * Math.PI * i / (frameSize - 1)));

  // Average magnitude spectrum
  const avgMag = new Float32Array(numBins);
  // Spectrogram: downsample time to ~50 columns, freq to ~40 rows (log scale)
  const specCols = Math.min(50, numFrames);
  const specRows = 40;
  const spectrogram = [];
  for (let r = 0; r < specRows; r++) spectrogram.push(new Float32Array(specCols));

  // Log frequency mapping: row 0 = low freq, row specRows-1 = high freq
  const minFreq = 40, maxFreq = sampleRate / 2;
  const freqBins = [];
  for (let r = 0; r < specRows; r++) {
    const f0 = minFreq * Math.pow(maxFreq / minFreq, r / specRows);
    const f1 = minFreq * Math.pow(maxFreq / minFreq, (r + 1) / specRows);
    freqBins.push([Math.floor(f0 * frameSize / sampleRate), Math.ceil(f1 * frameSize / sampleRate)]);
  }

  let totalRms = 0;
  let spectralCentroidSum = 0, spectralCentroidWeight = 0;
  let zeroCrossings = 0;

  for (let f = 0; f < numFrames; f++) {
    const frame = new Float32Array(frameSize);
    const off = f * hop;
    let rms = 0;
    for (let i = 0; i < frameSize; i++) {
      frame[i] = pcm[off + i] * win[i];
      rms += pcm[off + i] * pcm[off + i];
    }
    totalRms += Math.sqrt(rms / frameSize);

    const mag = fftMagnitude(frame, frameSize);
    for (let i = 0; i < numBins; i++) avgMag[i] += mag[i];

    // Spectral centroid
    let magSum = 0, freqWeightedSum = 0;
    for (let i = 0; i < numBins; i++) {
      const freq = i * sampleRate / frameSize;
      freqWeightedSum += freq * mag[i];
      magSum += mag[i];
    }
    if (magSum > 0) {
      spectralCentroidSum += freqWeightedSum / magSum;
      spectralCentroidWeight++;
    }

    // Fill spectrogram column
    const col = Math.min(specCols - 1, Math.floor(f / numFrames * specCols));
    for (let r = 0; r < specRows; r++) {
      const [b0, b1] = freqBins[r];
      let bandMag = 0;
      const count = Math.max(1, b1 - b0);
      for (let b = b0; b < Math.min(b1, numBins); b++) bandMag += mag[b];
      spectrogram[r][col] = Math.max(spectrogram[r][col], bandMag / count);
    }
  }

  // Zero crossings (on raw PCM)
  for (let i = 1; i < pcm.length; i++) {
    if ((pcm[i] >= 0) !== (pcm[i - 1] >= 0)) zeroCrossings++;
  }

  // Normalize
  for (let i = 0; i < numBins; i++) avgMag[i] /= numFrames;
  const avgRms = totalRms / numFrames;
  const spectralCentroid = spectralCentroidWeight > 0 ? spectralCentroidSum / spectralCentroidWeight : 0;
  const zeroCrossingRate = zeroCrossings / pcm.length * sampleRate;

  // Peak frequency
  let peakBin = 0, peakVal = 0;
  for (let i = 1; i < numBins; i++) {
    if (avgMag[i] > peakVal) { peakVal = avgMag[i]; peakBin = i; }
  }
  const peakFreq = peakBin * sampleRate / frameSize;

  // Downsample avgMag to specRows bands for display
  const bands = new Float32Array(specRows);
  for (let r = 0; r < specRows; r++) {
    const [b0, b1] = freqBins[r];
    let sum = 0, count = 0;
    for (let b = b0; b < Math.min(b1, numBins); b++) { sum += avgMag[b]; count++; }
    bands[r] = count > 0 ? sum / count : 0;
  }

  // Normalize spectrogram for display
  let specMax = 0;
  for (let r = 0; r < specRows; r++)
    for (let c = 0; c < specCols; c++)
      if (spectrogram[r][c] > specMax) specMax = spectrogram[r][c];
  if (specMax > 0) {
    for (let r = 0; r < specRows; r++)
      for (let c = 0; c < specCols; c++)
        spectrogram[r][c] /= specMax;
  }

  return {
    bands, spectrogram, specRows, specCols,
    rms: avgRms,
    spectralCentroid,
    peakFreq,
    zeroCrossingRate,
  };
}

async function analyzeSample(name, arrayBuffer, ctx) {
  try {
    const audioBuf = await ctx.decodeAudioData(arrayBuffer);
    const pcm = audioBuf.getChannelData(0);
    const duration = audioBuf.duration;
    const onsets = detectOnsets(pcm, audioBuf.sampleRate);
    const waveform = computeWaveform(pcm, 200);
    const spectrum = computeSpectrum(pcm, audioBuf.sampleRate);
    sampleMeta[name] = {
      duration,
      sampleRate: audioBuf.sampleRate,
      numChannels: audioBuf.numberOfChannels,
      numFrames: audioBuf.length,
      onsets,
      waveform,
      spectrum,
    };
    console.log('[tau-cam] Analyzed', name, '— duration:', duration.toFixed(3) + 's, onsets:', onsets.length);
  } catch (e) {
    console.warn('[tau-cam] Sample analysis failed for', name, e);
  }
}

// Expose sampleMeta for TreeNav ctx-info
window._sampleMeta = sampleMeta;

// Analyze a sample on demand (for TreeNav preview — no SuperSonic load)
const _analyzeQueue = new Set();
window._analyzeSampleOnDemand = async function(name) {
  if (sampleMeta[name]) return sampleMeta[name]; // already analyzed
  if (_analyzeQueue.has(name)) return null; // already in progress
  _analyzeQueue.add(name);
  try {
    let arrayBuffer;
    const entry = userSampleIndex[name];
    if (entry) {
      if (entry.serverPath) {
        const exts = ['.wav', '.flac', '.ogg', '.mp3', '.aiff'];
        for (const ext of exts) {
          try {
            const resp = await fetch(entry.serverPath + ext);
            if (resp.ok) { arrayBuffer = await resp.arrayBuffer(); break; }
          } catch {}
        }
      } else if (entry.file) {
        arrayBuffer = await entry.file.arrayBuffer();
      } else if (entry.fileHandle) {
        const file = await entry.fileHandle.getFile();
        arrayBuffer = await file.arrayBuffer();
      }
    } else {
      // Built-in sample — fetch from samples/ directory
      try {
        const resp = await fetch('samples/' + name + '.flac');
        if (resp.ok) arrayBuffer = await resp.arrayBuffer();
      } catch {}
    }
    if (!arrayBuffer) return null;
    // Decode using an OfflineAudioContext (doesn't need booted audio)
    const ctx = new OfflineAudioContext(1, 44100, 44100);
    await analyzeSample(name, arrayBuffer, ctx);
    return sampleMeta[name] || null;
  } catch (e) {
    console.warn('[tau-cam] On-demand analysis failed:', name, e);
    return null;
  } finally {
    _analyzeQueue.delete(name);
  }
};

// --- Preview play (for ctx-info play button) ---
window._previewSample = async function(name) {
  window._ctxInfoLocked = true;
  try {
    if (window._runSnippet) await window._runSnippet('sample :' + name);
  } finally {
    window._ctxInfoLocked = false;
  }
};

// --- Fetch built-in sample names for TreeNav ---
fetch('./api/samples').then(r => r.ok ? r.json() : []).then(names => {
  window._builtinSampleNames = names;
  console.log('[tau-cam] Built-in samples:', names.length);
}).catch(() => { window._builtinSampleNames = []; });

// --- Loading log (ASCII art + status) ---
const _asciiArt =
  '\u2591\u2588\u2580\u2580\u2591\u2588\u2591\u2588\u2591\u2588\u2580\u2588\u2591\u2588\u2580\u2580\u2591\u2588\u2580\u2584\u2591\u2588\u2580\u2580\u2591\u2588\u2580\u2588\u2591\u2588\u2580\u2588\u2591\u2580\u2588\u2580\u2591\u2588\u2580\u2580\n' +
  '\u2591\u2580\u2580\u2588\u2591\u2588\u2591\u2588\u2591\u2588\u2580\u2580\u2591\u2588\u2580\u2580\u2591\u2588\u2580\u2584\u2591\u2580\u2580\u2588\u2591\u2588\u2591\u2588\u2591\u2588\u2591\u2588\u2591\u2591\u2588\u2591\u2591\u2588\u2591\u2591\n' +
  '\u2591\u2580\u2580\u2580\u2591\u2580\u2580\u2580\u2591\u2580\u2591\u2591\u2591\u2580\u2580\u2580\u2591\u2580\u2591\u2580\u2591\u2580\u2580\u2580\u2591\u2580\u2580\u2580\u2591\u2580\u2591\u2580\u2591\u2580\u2580\u2580\u2591\u2580\u2580\u2580';

function showLoadingLog() {
  const el = document.getElementById('scope-loading');
  if (!el) return;
  el.classList.remove('crt-out', 'visible');
  el.innerHTML = '<div class="loading-center">' +
    '<div class="loading-ascii">' + _asciiArt + '</div>' +
    '<div class="loading-status">booting...</div></div>';
  void el.offsetWidth;
  el.classList.add('visible');
}

function hideLoadingLog() {
  const el = document.getElementById('scope-loading');
  if (!el || !el.classList.contains('visible')) return;
  el.classList.add('crt-out');
  const cleanup = () => {
    el.classList.remove('visible', 'crt-out');
    el.innerHTML = '';
  };
  el.addEventListener('animationend', cleanup, { once: true });
  setTimeout(cleanup, 700); // fallback if animationend doesn't fire
}

// --- User sample directories ---
const AUDIO_EXTENSIONS = new Set(['.wav', '.flac', '.ogg', '.mp3', '.aiff', '.aif']);
const sampleDirs = [];       // { handle, name: string, alias: string }
const userSampleIndex = {};  // relative path → { dirIdx, handle: FileSystemFileHandle }
window._userSampleIndex = userSampleIndex;
window._sampleDirs = sampleDirs;

function openSampleDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open('tau-cam-samples', 1);
    req.onupgradeneeded = () => req.result.createObjectStore('dirs');
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function saveSampleDirs() {
  const db = await openSampleDB();
  const tx = db.transaction('dirs', 'readwrite');
  const store = tx.objectStore('dirs');
  store.clear();
  for (let i = 0; i < sampleDirs.length; i++) {
    store.put({ handle: sampleDirs[i].handle, alias: sampleDirs[i].alias }, i);
  }
}

async function loadSampleDirs() {
  const db = await openSampleDB();
  const tx = db.transaction('dirs', 'readonly');
  const store = tx.objectStore('dirs');
  return new Promise((resolve) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => resolve([]);
  });
}

async function scanDirectory(dirHandle, prefix, dirIdx) {
  for await (const entry of dirHandle.values()) {
    if (entry.kind === 'file') {
      const ext = '.' + entry.name.split('.').pop().toLowerCase();
      if (AUDIO_EXTENSIONS.has(ext)) {
        const name = entry.name.replace(/\.[^.]+$/, '').trim();
        const relPath = prefix ? prefix + '/' + name : name;
        userSampleIndex[relPath] = { dirIdx, fileHandle: entry, ext };

      }
    } else if (entry.kind === 'directory') {
      const subPrefix = prefix ? prefix + '/' + entry.name : entry.name;
      await scanDirectory(entry, subPrefix, dirIdx);
    }
  }
}

async function addSampleDir() {
  const btn = document.getElementById('btn-add-sample-dir');
  const statusEl = document.getElementById('sample-dir-status');
  if (btn) { btn.disabled = true; btn.textContent = 'Scanning...'; }
  if (statusEl) statusEl.textContent = 'Scanning...';

  // Try File System Access API first, fall back to input[webkitdirectory]
  if (window.showDirectoryPicker) {
    try {
      console.log('[tau-cam] Opening directory picker...');
      const handle = await window.showDirectoryPicker();
      console.log('[tau-cam] Directory picked:', handle.name);
      sampleDirs.push({ handle, name: handle.name, alias: handle.name });
      showLoadingLog('Scanning ' + handle.name + '...');
      await scanDirectory(handle, '', sampleDirs.length - 1);
      const count = Object.keys(userSampleIndex).length;
      await saveSampleDirs();
      if (window._updateSampleDirsUI) window._updateSampleDirsUI();
      console.log('[tau-cam] Sample dir added:', handle.name, '—', count, 'samples indexed');
      syncSampleIndexToTauState();
      setTimeout(hideLoadingLog, 1500);
      if (btn) { btn.disabled = false; btn.textContent = 'Add Folder'; }
      return;
    } catch (e) {
      console.log('[tau-cam] showDirectoryPicker failed, falling back to input:', e.message);
    }
  }
  if (btn) { btn.disabled = false; btn.textContent = 'Add Folder'; }
  const statusEl2 = document.getElementById('sample-dir-status');
  if (statusEl2 && sampleDirs.length === 0) statusEl2.textContent = 'No folders';
  // Fallback: hidden input with webkitdirectory
  const input = document.createElement('input');
  input.type = 'file';
  input.webkitdirectory = true;
  input.multiple = true;
  input.onchange = () => {
    const files = input.files;
    if (!files || files.length === 0) return;
    // Derive dir name from first file's path
    const firstPath = files[0].webkitRelativePath;
    const dirName = firstPath.split('/')[0];
    const dirIdx = sampleDirs.length;
    sampleDirs.push({ handle: null, name: dirName, alias: dirName, files });
    showLoadingLog('Scanning ' + dirName + '...');
    for (const file of files) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!AUDIO_EXTENSIONS.has(ext)) continue;
      const relPath = file.webkitRelativePath.replace(/^[^/]+\//, '');
      const name = relPath.replace(/\.[^.]+$/, '');
      userSampleIndex[name] = { dirIdx, file, ext };
    }
    const count = Object.keys(userSampleIndex).length;
    if (window._updateSampleDirsUI) window._updateSampleDirsUI();
    console.log('[tau-cam] Sample dir added (fallback):', dirName, '—', count, 'samples indexed');
    syncSampleIndexToTauState();
    setTimeout(hideLoadingLog, 1500);
    const btn2 = document.getElementById('btn-add-sample-dir');
    if (btn2) { btn2.disabled = false; btn2.textContent = 'Add Folder'; }
  };
  input.click();
}

async function renameSampleDir(idx, newAlias) {
  if (idx >= 0 && idx < sampleDirs.length && newAlias) {
    sampleDirs[idx].alias = newAlias.trim();
    await saveSampleDirs();
    if (window._updateSampleDirsUI) window._updateSampleDirsUI();
  }
}
window._renameSampleDir = renameSampleDir;

async function removeSampleDir(idx) {
  const removed = sampleDirs.splice(idx, 1);
  // Rebuild index without the removed dir
  for (const key of Object.keys(userSampleIndex)) {
    if (userSampleIndex[key].dirIdx === idx) delete userSampleIndex[key];
    else if (userSampleIndex[key].dirIdx > idx) userSampleIndex[key].dirIdx--;
  }
  await saveSampleDirs();
  if (window._updateSampleDirsUI) window._updateSampleDirsUI();
}

async function restoreSampleDirs() {
  const entries = await loadSampleDirs();
  for (const entry of entries) {
    // Support both old format (raw handle) and new format ({handle, alias})
    const handle = entry.handle || entry;
    const alias = entry.alias || null;
    try {
      const perm = await handle.requestPermission({ mode: 'read' });
      if (perm === 'granted') {
        sampleDirs.push({ handle, name: handle.name, alias: alias || handle.name });
        await scanDirectory(handle, '', sampleDirs.length - 1);
      }
    } catch (e) {
      console.log('[tau-cam] Sample dir permission denied:', handle.name);
    }
  }
  if (sampleDirs.length > 0) {
    console.log('[tau-cam] Restored', sampleDirs.length, 'sample dirs,', Object.keys(userSampleIndex).length, 'samples');
  }
  if (window._updateSampleDirsUI) window._updateSampleDirsUI();
}

// Write all sample paths to tau-state so VMs can see them
function syncSampleIndexToTauState() {
  if (!audio) return; // not booted yet
  const beat = audio.ctx.currentTime * audio.BPM / 60;
  const paths = Object.keys(userSampleIndex);
  for (const p of paths) {
    audio.inboxRing.storeInt('/samples/' + p, beat, 0, 1);
  }
  if (paths.length > 0) {
    console.log('[tau-cam] Wrote', paths.length, 'sample paths to tau-state');
  }
}
window._syncSampleIndex = syncSampleIndexToTauState;

window._addSampleDir = addSampleDir;
window._removeSampleDir = removeSampleDir;

// Restore from IndexedDB (File System Access dirs)
restoreSampleDirs();

// Also fetch server-side user samples (symlinked dir)
fetch('./api/user-samples').then(r => r.ok ? r.json() : []).then(samples => {
  if (samples.length > 0) {
    const dirIdx = sampleDirs.length;
    sampleDirs.push({ handle: null, name: 'user-samples (server)' });
    for (const p of samples) {
      userSampleIndex[p] = { dirIdx, serverPath: '/user-samples/' + p, ext: '' };
    }
    console.log('[tau-cam] Server samples indexed:', samples.length);
    if (window._updateSampleDirsUI) window._updateSampleDirsUI();
  }
}).catch(() => {});

const BPM = 60;
const USER_BUS = 20;
const INBOX_BODY_SIZE = 1048576;
const RING_CAPACITY = 4096;
const RING_HEADER_BYTES = 8;
const RING_ENTRY_BYTES = 640;

// OSC encoder for forwarding SuperSonic replies into tau-state
const _oscEncBuf = new Uint8Array(512);
const _oscEncView = new DataView(_oscEncBuf.buffer);

function _oscStr(buf, off, str) {
  for (let i = 0; i < str.length; i++) buf[off++] = str.charCodeAt(i);
  buf[off++] = 0;
  while (off & 3) buf[off++] = 0;
  return off;
}

function encodeOscMsg(msg) {
  const buf = _oscEncBuf, dv = _oscEncView;
  let off = 0;
  off = _oscStr(buf, off, msg[0]);
  let tt = ',';
  for (let i = 1; i < msg.length; i++) {
    const v = msg[i];
    if (typeof v === 'number') tt += Number.isInteger(v) ? 'i' : 'f';
    else if (typeof v === 'string') tt += 's';
    else tt += 'N';
  }
  off = _oscStr(buf, off, tt);
  for (let i = 1; i < msg.length; i++) {
    const v = msg[i];
    if (typeof v === 'number' && Number.isInteger(v)) {
      dv.setInt32(off, v, false); off += 4;
    } else if (typeof v === 'number') {
      dv.setFloat32(off, v, false); off += 4;
    } else if (typeof v === 'string') {
      off = _oscStr(buf, off, v);
    }
  }
  return off;
}

// --- State ---
let audio = null;    // { ctx, node, sonic, inboxRing, ... }
let booting = false;
let compileWorker = null;
let eventWorker = null;

const btnRun = document.getElementById('btn-run');
const btnStop = document.getElementById('btn-stop');
const stdoutPaneEl = document.getElementById('stdout-pane');
const errorPaneEl = document.getElementById('error-pane');

let currentRuntimeErrors = [];
let stdoutLineCount = 0;

// --- OSC Log ---
const oscLogEl = document.getElementById('osc-log');
let oscLogCount = 0;
const OSC_LOG_MAX = 500;

function appendOscHtml(html) {
  if (!oscLogEl) return;
  const line = document.createElement('div');
  line.innerHTML = html;
  oscLogEl.appendChild(line);
  oscLogCount++;
  while (oscLogCount > OSC_LOG_MAX) {
    oscLogEl.removeChild(oscLogEl.firstChild);
    oscLogCount--;
  }
  oscLogEl.scrollTop = oscLogEl.scrollHeight;
}

function clearOscLog() {
  if (!oscLogEl) return;
  oscLogEl.innerHTML = '';
  oscLogCount = 0;
}

// --- Cue Log ---
const cueLogEl = document.getElementById('cue-log');
const logDividerEl = document.getElementById('log-divider');
let cueLogCount = 0;
const CUE_LOG_MAX = 200;
const knownCuePaths = new Set();
window._knownCuePaths = knownCuePaths;

function addCueLine(beat, path, value) {
  if (!cueLogEl) return;
  cueLogEl.style.display = 'block';
  if (logDividerEl) logDividerEl.style.display = 'block';
  knownCuePaths.add(path);
  const line = document.createElement('div');
  line.className = 'cue-line';
  line.innerHTML = `<span class="cue-beat">${beat.toFixed(3)}</span><span class="cue-path">${escHtml(path)}</span> <span class="cue-val">${escHtml(String(value)).replace(/(\d+\.\d{4,})/g, m => Number(m).toFixed(3))}</span>`;
  cueLogEl.appendChild(line);
  cueLogCount++;
  while (cueLogCount > CUE_LOG_MAX) {
    cueLogEl.removeChild(cueLogEl.firstChild);
    cueLogCount--;
  }
  cueLogEl.scrollTop = cueLogEl.scrollHeight;
}

function clearCueLog() {
  if (!cueLogEl) return;
  cueLogEl.innerHTML = '';
  cueLogEl.style.display = 'none';
  if (logDividerEl) logDividerEl.style.display = 'none';
  cueLogCount = 0;
  knownCuePaths.clear();
}

// --- Scope ---
const scopeCanvas = document.getElementById('scope-canvas');
const scopeCtx = scopeCanvas?.getContext('2d');
let scopeDataBuffer = null;
let scopeW = 0, scopeH = 0;
let analyser = null;
window._scopeRichGlow = true;

function resizeScope() {
  if (!scopeCanvas || !scopeCtx) return;
  const dpr = window.devicePixelRatio || 1;
  scopeW = scopeCanvas.offsetWidth;
  scopeH = scopeCanvas.offsetHeight;
  scopeCanvas.width = scopeW * dpr;
  scopeCanvas.height = scopeH * dpr;
  scopeCtx.setTransform(1, 0, 0, 1, 0, 0);
  scopeCtx.scale(dpr, dpr);
}

function scopeColor(prop) {
  return getComputedStyle(document.body).getPropertyValue(prop).trim();
}

function drawSilentScope() {
  if (!scopeCanvas || !scopeCtx) return;
  resizeScope();
  scopeCtx.fillStyle = scopeColor('--bg-panel');
  scopeCtx.fillRect(0, 0, scopeW, scopeH);
  scopeCtx.lineWidth = 2;
  scopeCtx.strokeStyle = scopeColor('--border-medium');
  scopeCtx.beginPath();
  scopeCtx.moveTo(0, scopeH / 2);
  scopeCtx.lineTo(scopeW, scopeH / 2);
  scopeCtx.stroke();
}

function drawScope() {
  if (!analyser || !scopeDataBuffer) return;
  requestAnimationFrame(drawScope);
  analyser.getByteTimeDomainData(scopeDataBuffer);

  const trail = window._scopeTrail !== undefined ? window._scopeTrail : 0.25;
  if (trail > 0) {
    // Semi-transparent fill for ghost trail effect
    scopeCtx.fillStyle = scopeColor('--bg-panel');
    scopeCtx.globalAlpha = 1 - trail;
    scopeCtx.fillRect(0, 0, scopeW, scopeH);
    scopeCtx.globalAlpha = 1;
  } else {
    scopeCtx.fillStyle = scopeColor('--bg-panel');
    scopeCtx.fillRect(0, 0, scopeW, scopeH);
  }

  const glow = window._scopeGlow !== undefined ? window._scopeGlow : 15;
  const accent = scopeColor('--accent');
  const lineW = window._scopeLineWidth || 2;

  scopeCtx.beginPath();
  const slice = scopeW / scopeDataBuffer.length;
  for (let i = 0; i < scopeDataBuffer.length; i++) {
    const y = (scopeDataBuffer[i] / 128.0) * scopeH / 2;
    i === 0 ? scopeCtx.moveTo(0, y) : scopeCtx.lineTo(i * slice, y);
  }
  scopeCtx.lineTo(scopeW, scopeH / 2);

  if (glow > 0 && window._scopeRichGlow) {
    // Real shadowBlur — looks better, costs more GPU
    scopeCtx.shadowBlur = glow;
    scopeCtx.shadowColor = accent;
    scopeCtx.lineWidth = lineW;
    scopeCtx.strokeStyle = accent;
    scopeCtx.stroke();
    scopeCtx.shadowBlur = 0;
    scopeCtx.shadowColor = 'transparent';
  } else {
    if (glow > 0) {
      // Fake glow — fat semi-transparent stroke underneath
      scopeCtx.lineWidth = lineW + glow;
      scopeCtx.strokeStyle = accent;
      scopeCtx.globalAlpha = 0.15;
      scopeCtx.stroke();
      scopeCtx.globalAlpha = 1;
    }
    scopeCtx.lineWidth = lineW;
    scopeCtx.strokeStyle = accent;
    scopeCtx.stroke();
  }
}

function setupScope(ctx, sonicNode) {
  window.removeEventListener('resize', drawSilentScope);
  analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0.8;
  scopeDataBuffer = new Uint8Array(analyser.fftSize);

  // Chain: sonicNode → analyser → destination
  sonicNode.disconnect();
  sonicNode.connect(analyser);
  analyser.connect(ctx.destination);

  resizeScope();
  window.addEventListener('resize', resizeScope);
  drawScope();
}

drawSilentScope();
window.addEventListener('resize', drawSilentScope);

// Tap scope to toggle half-height
(function() {
  const scopeContainer = document.getElementById('scope-container');
  if (!scopeContainer) return;
  scopeContainer.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    scopeContainer.classList.toggle('half');
    requestAnimationFrame(() => {
      resizeScope();
      drawSilentScope();
    });
  });
})();

// Redraw silent scope on theme change (live scope redraws every frame already)
window._redrawScope = () => { if (!analyser) drawSilentScope(); };

// --- Stdout pane ---
function escHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function addStdoutLine(beat, text) {
  if (!stdoutPaneEl) return;
  stdoutPaneEl.style.display = 'block';
  const line = document.createElement('div');
  line.className = 'stdout-line';
  line.innerHTML = `<span class="stdout-beat">${beat.toFixed(3)}</span>${escHtml(text)}`;
  stdoutPaneEl.appendChild(line);
  stdoutLineCount++;
  while (stdoutLineCount > 200) {
    stdoutPaneEl.removeChild(stdoutPaneEl.firstChild);
    stdoutLineCount--;
  }
  stdoutPaneEl.scrollTop = stdoutPaneEl.scrollHeight;
}

function clearStdout() {
  if (!stdoutPaneEl) return;
  stdoutPaneEl.innerHTML = '';
  stdoutPaneEl.style.display = 'none';
  stdoutLineCount = 0;
}

// --- Error pane (runtime errors) ---
function addRuntimeError(beat, msg) {
  currentRuntimeErrors.push(`[${beat.toFixed(3)}] ${msg}`);
  if (currentRuntimeErrors.length > 20) {
    currentRuntimeErrors = currentRuntimeErrors.slice(-20);
  }
  renderRuntimeErrors();
}

function renderRuntimeErrors() {
  if (!errorPaneEl) return;
  if (currentRuntimeErrors.length === 0) {
    errorPaneEl.style.display = 'none';
    errorPaneEl.innerHTML = '';
    return;
  }
  errorPaneEl.style.display = 'block';
  errorPaneEl.innerHTML = currentRuntimeErrors
    .map(e => `<div class="error-line">${escHtml(e)}</div>`)
    .join('');
}

function clearRuntimeErrors() {
  currentRuntimeErrors = [];
  renderRuntimeErrors();
}

// --- Compile worker ---
// Compile-worker message dispatch.
//
// The worker's `onmessage` is a single slot, so the previous code (each
// caller assigning compileWorker.onmessage = handler) was racy: a second
// caller would clobber an in-flight caller's listener and drop its reply.
// We install one permanent dispatcher and route replies to per-type FIFO
// resolver queues. Worker requests are processed in order, so FIFO matches
// arrival order of the corresponding replies.
const compilePending = Object.create(null); // type -> resolver[]

function compileWorkerDispatch(e) {
  const t = e.data && e.data.type;
  if (!t) return;
  const queue = compilePending[t];
  if (queue && queue.length > 0) {
    queue.shift()(e.data);
    return;
  }
  // Unsolicited push (no caller waiting): swallow, but surface worker errors.
  if (t === 'error') {
    console.error('[tau-cam] compile worker error:', e.data.message);
    window._debugLog?.('compile-worker-error', e.data.message);
  }
}

function compileWorkerWait(type) {
  return new Promise((resolve) => {
    (compilePending[type] = compilePending[type] || []).push(resolve);
  });
}

function initCompileWorker() {
  compileWorker = new Worker('./tau-state/editor_worker.js', { type: 'module' });
  compileWorker.onerror = (e) => {
    console.error('[tau-cam] Compile worker crashed:', e);
    window._debugLog?.('compile-worker-crash', e.message || 'unknown');
    compileWorker = null;
    // Drop any pending resolvers — they'd never hear from this dead worker.
    for (const k in compilePending) compilePending[k].length = 0;
    // Auto-restart
    initCompileWorker().then(() => {
      console.log('[tau-cam] Compile worker restarted');
      window._debugLog?.('compile-worker-restarted');
    });
  };
  compileWorker.onmessage = compileWorkerDispatch;
  const ready = compileWorkerWait('ready');
  compileWorker.postMessage({ type: 'config' });
  return ready;
}

function compileSource(source, { stmtIdStart = 1 } = {}) {
  // Post first, register listener after. If compileWorker is null (e.g. the
  // precompile timer fires before ensureBooted), postMessage throws sync
  // and the resolver never gets enqueued — so a failed call can't orphan a
  // slot in compilePending.result and hang the next real run.
  compileWorker.postMessage({ type: 'parse', source, stmtIdStart });
  return compileWorkerWait('result');
}

// --- Event worker (timeline ring → print/error filtering) ---
let _lastTimelineRingSAB = null;

function initEventWorker(timelineRingSAB) {
  if (timelineRingSAB) _lastTimelineRingSAB = timelineRingSAB;
  eventWorker = new Worker('./tau-state/event_worker.js');
  eventWorker.onerror = (e) => {
    console.error('[tau-cam] Event worker crashed:', e);
    window._debugLog?.('event-worker-crash', e.message || 'unknown');
    eventWorker = null;
    if (_lastTimelineRingSAB) {
      initEventWorker();
      console.log('[tau-cam] Event worker restarted');
      window._debugLog?.('event-worker-restarted');
    }
  };
  eventWorker.postMessage({ type: 'init', buffer: _lastTimelineRingSAB, capacity: RING_CAPACITY });

  // Buffer events and flush via rAF so touch/UI events aren't starved
  let _eventQueue = [];
  let _eventRafPending = false;

  function _flushEvents() {
    _eventRafPending = false;
    const queue = _eventQueue;
    _eventQueue = [];
    for (let i = 0; i < queue.length; i++) {
      const ev = queue[i];
      // UUID → stmtId map is populated eagerly in onmessage (above)
      if (ev.path === '/vm/error' && typeof ev.value === 'string') {
        addRuntimeError(ev.beat, ev.value);
      }
      if (ev.path === '/print' && typeof ev.value === 'string') {
        addStdoutLine(ev.beat, ev.value);
      }
      if (ev.path === '/log' && typeof ev.value === 'string') {
        {
          let t = escHtml(ev.value);
          t = t.replace(/(\d+\.\d{4,})/g, m => Number(m).toFixed(3));
          // Colorize: apply number spans FIRST on plain text, then wrap structures around them
          t = t.replace(/\b(\d+\.?\d*)\b/g, '<span class="osc-num">$1</span>');
          t = t.replace(/\[([^\]]+)\]/g, '<span class="osc-loop">[$1]</span>');
          t = t.replace(/:(\w+)/g, '<span class="osc-sym">:$1</span>');
          appendOscHtml(`<span class="osc-ts">${ev.beat.toFixed(3)}</span> ${t}`);
        }
        // Route to live loop visualization
        if (ev.userLabel && window.editor?.pushLoopEvent) {
          const isSample = ev.value.startsWith('sample');
          const cs = getComputedStyle(document.body);
          const color = isSample
            ? (cs.getPropertyValue('--color-loop-sample').trim() || '#ff8c00')
            : (cs.getPropertyValue('--color-loop-synth').trim() || '#ff1493');
          const noteMatch = ev.value.match(/note:\s*([\d.]+)/);
          const note = isSample ? -1 : (noteMatch ? parseFloat(noteMatch[1]) : 60);
          // Samples: short fixed duration (one-shot, no sustain phase).
          // Synths: null = open until /n_end closes them.
          const dur = isSample ? 150 : null;
          window.editor.pushLoopEvent(ev.userLabel, color, dur, note);
        }
      }
      if (ev.path && ev.path.startsWith('/') &&
          !ev.path.startsWith('/vm/') && !ev.path.startsWith('/print') &&
          !ev.path.startsWith('/log') &&
          !ev.path.startsWith('/supersonic/') && !ev.path.startsWith('/buf/') &&
          !ev.path.startsWith('/samples/') &&
          !ev.path.startsWith('/diagnostics/')) {
        addCueLine(ev.beat, ev.path, ev.value);
      }
      if (ev.userLabel) {
        // Flash at the time the note actually plays, not when the VM executed it.
        // For time_warp events, the beat is in the future — delay the flash.
        const flashFn = () => {
          // IDs are namespaced: main editor starts at 1, snippets at 1,000,000
          // Both lookups are safe — non-matching IDs are no-ops
          if (window._snippetStmtMap && window._snippetHighlight) {
            const entry = window._snippetStmtMap.get(ev.userLabel);
            if (entry) window._snippetHighlight(entry.start, entry.end);
          }
          if (window.editor?.highlightStatement) {
            window.editor.highlightStatement(ev.userLabel);
          }
        };
        if (audio) {
          const currentBeat = audio.ctx.currentTime * audio.BPM / 60;
          const delayMs = (ev.beat - currentBeat) * 60000 / audio.BPM;
          if (delayMs > 15) {
            setTimeout(flashFn, delayMs);
          } else {
            flashFn();
          }
        } else {
          flashFn();
        }
      }
    }
  }

  eventWorker.onmessage = (e) => {
    const msg = e.data;
    if (msg.type !== 'events') return;
    const batch = msg.batch;
    // Populate UUID → stmtId map eagerly (before rAF) so /n_go can find entries
    for (let i = 0; i < batch.length; i++) {
      const ev = batch[i];
      if (ev.path === '/supersonic/osc' && ev.osc) {
        const msgs = ev.osc.kind === 'bundle' ? (ev.osc.messages || []) : [ev.osc];
        for (const m of msgs) {
          if (m.address === '/s_new' && m.args?.[1] && ev.userLabel) {
            const arg1 = m.args[1];
            const key = arg1.type === 'u' ? arg1.value       // legacy UUID
                      : arg1.type === 'i' ? arg1.value       // int32 node ID
                      : (typeof arg1 === 'number') ? arg1    // bare number
                      : null;
            if (key !== null) _synthUuidToStmt.set(key, ev.userLabel);
          }
        }
      }
      _eventQueue.push(ev);
    }
    if (!_eventRafPending) {
      _eventRafPending = true;
      requestAnimationFrame(_flushEvents);
    }
  };
}

// --- Audio boot ---
async function bootAudio() {
  showLoadingLog('/boot');

  // 1. Boot tau-state AudioWorklet
  const [bytes, randStreamBytes] = await Promise.all([
    fetch('./tau-state/tau_state.wasm').then(r => r.arrayBuffer()),
    fetch('./tau-state/rand_stream.bin').then(r => r.arrayBuffer()),
  ]);
  const wasmModule = await WebAssembly.compile(bytes);

  const ctx = new AudioContext();
  await ctx.audioWorklet.addModule('./tau-state/tau_processor.js');

  const node = new AudioWorkletNode(ctx, 'tau-processor', {
    processorOptions: {
      wasmModule,
      randStreamBytes,
      config: { bpm: BPM },
    },
  });
  node.connect(ctx.destination);

  // Set up SharedArrayBuffers
  const metricsSAB = new SharedArrayBuffer(8192); // 1024 Float64 slots
  const metricsView = new Float64Array(metricsSAB);

  const timelineRingSAB = new SharedArrayBuffer(
    RING_HEADER_BYTES + RING_CAPACITY * RING_ENTRY_BYTES
  );

  const inboxSAB = new SharedArrayBuffer(8 + INBOX_BODY_SIZE);
  const inboxRing = new InboxWriter(inboxSAB);

  node.port.postMessage({ type: 'initMetrics', buffer: metricsSAB });
  node.port.postMessage({ type: 'initTimeline', buffer: timelineRingSAB, capacity: RING_CAPACITY });
  node.port.postMessage({ type: 'initInbox', buffer: inboxSAB });
  node.port.onmessage = (e) => {
    if (e.data?.type === 'error') {
      console.error('[tau-cam] tau-processor error:', e.data);
      window._debugLog?.('tau-processor-error', e.data.error || e.data);
    }
    if (e.data?.type === 'diagnostic') {
      const d = e.data;
      window._debugLog?.('tau-worklet', d.kind + ': gap=' + d.gapMs + 'ms beat=' +
        (d.beat?.toFixed(3) ?? '?') + ' prev=' + (d.prevBeat?.toFixed(3) ?? '?') +
        ' tick=' + (d.tick ?? '?') + ' t=' + (d.currentTime?.toFixed(3) ?? '?'));
    }
  };

  console.log('[tau-cam] AudioWorklet initialized, BPM=' + BPM);

  // Start event worker
  initEventWorker(timelineRingSAB);

  // 2. Boot SuperSonic
  const sonic = new SuperSonic({
    mode: 'sab',
    baseURL: './dist/',
    audioContext: ctx,
    sampleBaseURL: './samples/',
    synthdefBaseURL: './synthdefs/',
    memory: {
      bufferPoolSize: 122 * 1024 * 1024,
    },
    scsynthOptions: {
      numAudioBusChannels: 1024,
      realTimeMemorySize: 131072, // 128MB — matches Sonic Pi's `-m 131072`
    },
    debug: false,
  });

  await sonic.init();
  await sonic.send('/notify', 1);

  const bufferInfo = {};
  const sampleBufnums = {};
  let nextBufnum = 10;

  // Track synth completion via /n_end.
  // SuperSonic no longer has a UUID rewriter — node IDs are int32.
  // Dispatch /synth_ended to tau-state so the synth pool is cleaned up
  // and GC can free FX chains and buses.
  sonic.on('in', (msg) => {
    if (msg[0] === '/n_end' && msg.length > 1) {
      try {
        const nodeId = (typeof msg[1] === 'number') ? msg[1]
                     : (msg[1]?.type === 'uuid') ? msg[1].value  // legacy path
                     : null;
        if (nodeId === null) return;

        if (typeof nodeId === 'number') {
          // Int32 node ID — encode as 16-byte blob with hi=0, lo=id
          const blob = new Uint8Array(16);
          const view = new DataView(blob.buffer);
          view.setUint32(12, nodeId, false); // big-endian in lo bytes
          inboxRing.synthEnded(blob);
        } else {
          // Legacy UUID bytes
          inboxRing.synthEnded(nodeId);
        }

        // Close the piano roll bar for this synth
        const lookupKey = (typeof nodeId === 'number') ? nodeId : nodeId;
        const stmtId = _synthUuidToStmt.get(lookupKey);
        if (stmtId && window.editor?.endLoopEvent) {
          window.editor.endLoopEvent(stmtId);
          _synthUuidToStmt.delete(lookupKey);
        }
      } catch (e) {
        console.error('[tau-cam] synthEnded error:', e);
      }
    }
  });

  await sonic.loadSynthDefs([
    'sonic-pi-basic_stereo_player',
    'sonic-pi-basic_mono_player',
    'sonic-pi-beep',
    'sonic-pi-mixer',
    'sonic-pi-basic_mixer',  // per-run ×0.3 pad ahead of the master FX chain
    'sonic-pi-fx_hpf',
    'sonic-pi-fx_lpf',
    'sonic-pi-fx_reverb',
    'sonic-pi-fx_level',  // per-loop mixer (routes loop_bus → main bus)
    'sonic-pi-scope',     // per-bus scope (ScopeOut2 → SAB triple-buffer)
  ]);

  // Bus routing:
  //   synths → bus 20 (USER_BUS) → job_mixer (×0.3) → bus 26 → HPF → 22 → LPF → 24 → master mixer → 0
  //
  // The job_mixer (sonic-pi-basic_mixer with amp=0.3) is the missing
  // ~-10.5 dB pad that Sonic Pi inserts per-run. Without it, loud synths
  // (`synth :rodeo, amp: 5`) overdrive downstream FX (especially
  // waveshapers like :krush) and the result is audibly harsher than the
  // same code played in Sonic Pi. See studio.rb:job_mixer — Sonic Pi
  // creates a `basic_mixer` with `amp: 0.3` for every run's bus.
  const BUS_JOB_OUT = 26;   // stereo pair 26,27 — output of the per-run pad
  const BUS_HPF_OUT = 22;
  const BUS_LPF_OUT = 24;

  // FX node IDs (fixed, outside VM range)
  const FX_JOB   = 204;
  const FX_HPF   = 201;
  const FX_LPF   = 202;
  const FX_MIXER = 203;

  // Create groups + FX chain
  async function createGroupsAndFx() {
    await sonic.send('/g_new', 100, 0, 0);   // synth group (head of root)
    await sonic.send('/g_new', 200, 1, 0);   // FX group (tail of root)
    // Job mixer (−10.5 dB pad): bus 20 → bus 26
    await sonic.send('/s_new', 'sonic-pi-basic_mixer', FX_JOB, 0, 200,
      'in_bus', USER_BUS, 'out_bus', BUS_JOB_OUT,
      'amp', 0.3);
    // HPF: bus 26 → bus 22
    await sonic.send('/s_new', 'sonic-pi-fx_hpf', FX_HPF, 3, FX_JOB,
      'in_bus', BUS_JOB_OUT, 'out_bus', BUS_HPF_OUT,
      'cutoff', 10);
    // LPF: bus 22 → bus 24
    await sonic.send('/s_new', 'sonic-pi-fx_lpf', FX_LPF, 3, FX_HPF,
      'in_bus', BUS_HPF_OUT, 'out_bus', BUS_LPF_OUT,
      'cutoff', 130.0, 'res', 0.5);
    // Master mixer (safety): bus 24 → bus 0 (main out)
    await sonic.send('/s_new', 'sonic-pi-mixer', FX_MIXER, 3, FX_LPF,
      'in_bus', BUS_LPF_OUT, 'out_bus', 0,
      'amp', parseFloat(localStorage.getItem('tau-pref:volume') || '0.8'),
      'pre_amp', 0.3, 'hpf', 21, 'lpf', 135.5, 'limiter_bypass', 0);

    // Tell tau-state how to allocate per-run mixers. Each run's mixer is
    // spawned at the head of group 200 (the FX group), reads from a bus
    // allocated from tau-state's pool, and writes to USER_BUS=20 so the
    // existing FX_JOB → HPF → LPF → master chain picks it up unchanged.
    //
    // fadeBeats controls only the old run's amp_slide ramp before
    // /g_freeAll; the new run still starts at `beat` regardless, so this
    // is pure cleanup time — safe to make generous.
    //   0.04 beats ≈ 40ms @ 60bpm, 20ms @ 120bpm, 18ms @ 130bpm.
    // That's enough samples (~900 @ 48kHz) for amp_slide to ramp cleanly
    // without clicks when the group is then freed. Shorter values (0.01)
    // caused audible clicks on reset because /g_freeAll landed before
    // the mixer finished ramping.
    try {
      inboxRing.configRun('sonic-pi-fx_level', 200, USER_BUS, 0.08);
    } catch (_) {}
  }

  await createGroupsAndFx();

  // Re-transfer OscChannel on reload
  sonic.on('reload:complete', async () => {
    await createGroupsAndFx();
    const oscChannel = sonic.osc.createOscChannel({ blocking: false });
    node.port.postMessage(
      { type: 'oscChannel', channelData: oscChannel.transferable },
      oscChannel.transferList
    );
  });

  // --- Lifecycle: handle audio suspension/resumption ---
  const suspendedOverlay = document.getElementById('suspended-overlay');

  function showSuspended() {
    suspendedOverlay?.classList.add('visible');
  }

  function hideSuspended() {
    suspendedOverlay?.classList.remove('visible');
  }

  sonic.on('audiocontext:suspended', () => {
    window._debugLog?.('ac-suspended', 'state=' + ctx.state);
    handleStop();
    showSuspended();
  });

  sonic.on('audiocontext:interrupted', () => {
    window._debugLog?.('ac-interrupted', 'state=' + ctx.state);
    handleStop();
    showSuspended();
  });

  sonic.on('audiocontext:resumed', () => {
    window._debugLog?.('ac-resumed', 'state=' + ctx.state);
    hideSuspended();
  });

  sonic.on('audiocontext:statechange', ({ state }) => {
    window._debugLog?.('ac-statechange', state);
  });

  sonic.on('error', (err) => {
    console.error('[tau-cam] SuperSonic error:', err);
    window._debugLog?.('supersonic-error', err?.message || String(err));
  });

  sonic.on('debug', (msg) => {
    window._debugLog?.('supersonic-debug', typeof msg === 'string' ? msg : JSON.stringify(msg));
  });

  sonic.on('in:text', ({ text }) => {
    window._debugLog?.('osc-in', text);
  });

  sonic.on('out:text', ({ text }) => {
    window._debugLog?.('osc-out', text);
  });

  // Structured OSC capture for crash report replay
  const OSC_CAP_MAX = 500;
  const _oscCapture = [];
  sonic.on('out', (msg) => {
    _oscCapture.push({ t: performance.now(), dir: 'out', msg });
    if (_oscCapture.length > OSC_CAP_MAX) _oscCapture.shift();
  });
  sonic.on('in', (msg) => {
    _oscCapture.push({ t: performance.now(), dir: 'in', msg });
    if (_oscCapture.length > OSC_CAP_MAX) _oscCapture.shift();
  });
  window._getOscCapture = () => {
    if (_oscCapture.length === 0) return [];
    const t0 = _oscCapture[0].t;
    return _oscCapture.map(e => ({
      ms: Math.round((e.t - t0) * 100) / 100,
      dir: e.dir,
      msg: e.msg,
    }));
  };

  // Detect worklet death via processorerror
  node.addEventListener('processorerror', (e) => {
    console.error('[tau-cam] AudioWorklet processor error:', e);
    window._debugLog?.('processorerror', e?.message || 'processor crashed');
    handleStop();
    showSuspended();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && ctx.state === 'suspended') {
      showSuspended();
    }
  });

  // Tau worklet health monitor — detect silent death
  let _lastTauTick = 0;
  let _tauDeathLogged = false;
  setInterval(() => {
    const tick = metricsView[0]; // M_TICK slot
    if (ctx.state === 'running' && tick > 0) {
      if (tick === _lastTauTick && !_tauDeathLogged) {
        _tauDeathLogged = true;
        const preTick = metricsView[39]; // M_PRE_TICK slot
        const cause = preTick > tick ? 'wasm_hung' : 'browser_stopped';
        const phase = metricsView[42];
        const phaseLabel = {0.5:'inbox',1:'pre_tau_tick',2:'pre_exec',3:'executing',4:'osc_flush',5:'spawn',6:'tset',7:'result',8:'trim',9:'tau_tick_done',10:'ss_pre_query',11:'ss_post_query',12:'ss_send',13:'ring_pre',14:'ring_done',15:'read_instr',16:'pre_metrics',100:'vm_loop_enter',101:'sync_check',102:'event_list_walk',103:'tracker_cleanup',104:'cue_emit',110:'loop_restart_entry',111:'check_pending_bc'}[phase] || 'unknown(' + phase + ')';
        window._debugLog?.('tau-worklet-death',
          'CRASH: tick=' + tick +
          ' preTick=' + preTick +
          ' cause=' + cause +
          ' phase=' + phaseLabel + '(' + phase + ')' +
          ' vmIdx=' + metricsView[43] +
          ' pc=' + metricsView[44] +
          ' op=' + metricsView[45] +
          ' instr=' + metricsView[46] +
          ' result=' + metricsView[47] +
          ' vmSlots=' + metricsView[21] +
          ' groupKids=' + metricsView[23] +
          ' heap=' + metricsView[6] +
          ' heapCorrupt=' + metricsView[38] +
          ' t=' + ctx.currentTime.toFixed(3));
        handleStop();
      }
      _lastTauTick = tick;
    }
  }, 2000);

  const btnResume = document.getElementById('btn-resume');
  if (btnResume) {
    btnResume.onclick = async () => {
      window._debugLog?.('resume-click', 'state=' + ctx.state);
      btnResume.textContent = 'Resuming...';
      btnResume.disabled = true;
      const t0 = performance.now();
      try {
        const ok = await sonic.recover();
        const ms = Math.round(performance.now() - t0);
        window._debugLog?.('resume-done', 'ok=' + ok + ' state=' + ctx.state + ' took=' + ms + 'ms');
        if (ok) hideSuspended();
      } catch (e) {
        const ms = Math.round(performance.now() - t0);
        window._debugLog?.('resume-error', (e?.message || String(e)) + ' state=' + ctx.state + ' took=' + ms + 'ms');
        console.error('[tau-cam] Recovery failed:', e);
      }
      btnResume.textContent = 'Resume Audio';
      btnResume.disabled = false;
    };
  }

  // Transfer OscChannel to worklet
  const oscChannel = sonic.osc.createOscChannel({ blocking: false });
  node.port.postMessage(
    { type: 'oscChannel', channelData: oscChannel.transferable },
    oscChannel.transferList
  );

  // Expose send for touch pad FX control
  window._sonicSend = (...args) => sonic.send(...args);
  window._FX_HPF = FX_HPF;
  window._FX_LPF = FX_LPF;
  window._FX_MIXER = FX_MIXER;

  console.log('[tau-cam] SuperSonic ready');

  // Expose sonic for debug panel — metrics connect on-demand (not always polling)
  window._sonicInstance = sonic;

  // Expose tau processor metrics for crash reports
  window._getTauProcessorReport = () => {
    const v = metricsView;
    const tick = v[0];       // M_TICK — written at END of process()
    const preTick = v[39];   // M_PRE_TICK — written at START of process()
    let diagnosis = 'running';
    if (tick > 0 && preTick > tick) diagnosis = 'wasm_hung';
    else if (tick > 0 && preTick === tick) diagnosis = 'browser_stopped';
    return {
      tick,
      preTick,
      diagnosis,
      beat: v[1],
      processUs: v[2],
      processPeakUs: v[3],
      msgsTotal: v[4],
      msgsTick: v[5],
      heapBytes: v[6],
      audioTime: v[7],
      bpm: v[8],
      eventsFired: v[9],
      queriesTick: v[10],
      overruns: v[11],
      instructionsTick: v[12],
      vmCount: v[13],
      eventCount: v[14],
      eventCapacity: v[15],
      nodeCount: v[16],
      nodeCapacity: v[17],
      heapCapacity: v[18],
      stringCount: v[19],
      stringCapacity: v[20],
      vmSlotsUsed: v[21],
      vmCapacity: v[22],
      groupChildren: v[23],
      groupCapacity: v[24],
      oscEventsTick: v[25],
      oscWriteFails: v[26],
      arenaSize: v[27],
      shaTableCount: v[28],
      shaTableCapacity: v[29],
      synthPoolCount: v[30],
      synthPoolCapacity: v[31],
      trackerPoolUsed: v[32],
      trackerPoolCapacity: v[33],
      heapCorruptions: v[50],
      heapCorruptOffset: v[51],
      heapCorruptReason: {1:'OOB',2:'depth',3:'double_free',4:'bad_refcount'}[v[52]] || v[52],
      gapPeakUs: v[40],
      wasmErrors: v[41],
      // Extended em[] block lives at SAB[48..695] via offset (i-32). em[126] → SAB[142].
      busAvailable: v[142],         // em[126] = bus_alloc.available()
      gcRegisterFailures: v[143],   // em[127]
      nEndGroupCount: v[144],       // em[128]
      nEndSynthMiss: v[145],        // em[129]
      trace: {
        phase: v[42],
        phaseLabel: {0.5:'inbox',1:'pre_tau_tick',2:'pre_exec',3:'executing',4:'osc_flush',5:'spawn',6:'tset',7:'result',8:'trim',9:'tau_tick_done',10:'ss_pre_query',11:'ss_post_query',12:'ss_send',13:'ring_pre',14:'ring_done',15:'read_instr',16:'pre_metrics'}[v[42]] || 'unknown(' + v[42] + ')',
        vmIndex: v[43],
        vmPc: v[44],
        opcode: v[45],
        instructionCount: v[46],
        result: v[47],
        resultLabel: {1:'halt',2:'sleep',4:'loop_restart',5:'budget_exhausted'}[v[47]] || '',
      },
      // Per-VM dump: name + loop_bus (em[200..631], 18 fields per VM, up to 24)
      vmDump: (() => {
        const out = [];
        for (let i = 0; i < 24; i++) {
          const base = 216 + i * 18; // em[200] → SAB[216] (offset +16)
          const state = v[base + 1];
          if (state === undefined) break;
          const nameLen = v[base + 8];
          let name = '';
          if (nameLen > 0) {
            const n = Math.min(nameLen, 32);
            for (let k = 0; k < n; k++) {
              const slot = 10 + (k >> 2);
              const shift = (k & 3) * 8;
              const ch = (v[base + slot] >> shift) & 0xFF;
              if (ch > 0) name += String.fromCharCode(ch);
            }
          }
          out.push({
            idx: v[base],
            state: ['free','run','readWait','aheadWait','trackWait'][state] || '?',
            poolGen: v[base + 2],
            loopBus: v[base + 9],
            name: name || null,
          });
        }
        return out.filter(vm => vm.state !== 'free');
      })(),
    };
  };

  // --- Per-loop scopes ---
  // Creates a sonic-pi-scope synth for each named live_loop, reading from
  // its loop_bus and writing to a scope slot in the SAB. Read via sonic.getScope(n).
  const _scopeNodes = new Map();  // loopName → { nodeId, scopeNum, bus }
  let _nextScopeNum = 0;

  window._scopeLoops = () => {
    const report = window._getTauProcessorReport();
    if (!report || !report.vmDump) return {};

    const activeLoops = report.vmDump.filter(vm => vm.name && vm.loopBus > 0);
    const result = {};

    for (const vm of activeLoops) {
      if (_scopeNodes.has(vm.name)) {
        // Already scoped — update bus if changed
        const existing = _scopeNodes.get(vm.name);
        if (existing.bus !== vm.loopBus) {
          sonic.send('/n_set', existing.nodeId, 'bus', vm.loopBus);
          existing.bus = vm.loopBus;
        }
        result[vm.name] = { scopeNum: existing.scopeNum, bus: vm.loopBus };
      } else {
        // Create new scope synth
        const scopeNum = _nextScopeNum++;
        const nodeId = sonic.nextNodeId();
        sonic.send('/s_new', 'sonic-pi-scope', nodeId, 1, 0,
          'bus', vm.loopBus, 'scope_num', scopeNum, 'max_frames', 1024);
        _scopeNodes.set(vm.name, { nodeId, scopeNum, bus: vm.loopBus });
        result[vm.name] = { scopeNum, bus: vm.loopBus };
      }
    }

    return result;
  };

  /**
   * Read all loop scopes. Returns { loopName: { bus, peak, left, right }, ... }
   */
  window._readLoopScopes = () => {
    const result = {};
    for (const [name, info] of _scopeNodes) {
      const data = sonic.getScope(info.scopeNum);
      if (!data) {
        result[name] = { bus: info.bus, peak: 0, active: false };
        continue;
      }
      let peak = 0;
      for (let i = 0; i < data.left.length; i++) {
        const a = Math.abs(data.left[i]);
        if (a > peak) peak = a;
      }
      result[name] = { bus: info.bus, peak, active: true, left: data.left, right: data.right };
    }
    return result;
  };

  // Live synth count display
  const synthCountEl = document.getElementById('synth-count');
  if (synthCountEl) {
    let _synthCountRAF = 0;
    const updateSynthCount = () => {
      const total = metricsView[30];
      if (total > 0) {
        synthCountEl.textContent = total + ' synths';
      } else {
        synthCountEl.textContent = '';
      }
      _synthCountRAF = requestAnimationFrame(updateSynthCount);
    };
    _synthCountRAF = requestAnimationFrame(updateSynthCount);
  }

  // Sync any already-scanned sample dirs to tau-state
  syncSampleIndexToTauState();

  // Set up scope visualizer (on SuperSonic's audio output, not tau-state worklet)
  setupScope(ctx, sonic.node);

  async function ensureSampleLoaded(name) {
    if (sampleBufnums[name]) return sampleBufnums[name];
    const bufnum = nextBufnum++;
    console.log('[tau-cam] Loading sample:', name);

    // Check user sample dirs first
    let userEntry = userSampleIndex[name];
    // If name is a directory prefix (not an exact match), resolve with index 0
    if (!userEntry && name.includes('/')) {
      const resolved = resolveSampleName(name + '|0');
      if (resolved !== name + '|0') {
        console.log('[tau-cam] Auto-resolved dir to:', resolved);
        userEntry = userSampleIndex[resolved];
        if (userEntry) {
          sampleBufnums[name] = bufnum; // cache under original name too
        }
      }
    }
    if (userEntry) {
      let arrayBuffer;
      if (userEntry.serverPath) {
        // Server-served sample — fetch via HTTP
        const exts = ['.wav', '.flac', '.ogg', '.mp3', '.aiff'];
        for (const ext of exts) {
          try {
            const resp = await fetch(userEntry.serverPath + ext);
            if (resp.ok) { arrayBuffer = await resp.arrayBuffer(); break; }
          } catch {}
        }
        if (!arrayBuffer) {
          console.warn('[tau-cam] Could not load user sample:', name);
          return bufnum;
        }
      } else {
        const file = userEntry.file || await userEntry.fileHandle.getFile();
        arrayBuffer = await file.arrayBuffer();
      }
      const analysisBuffer = arrayBuffer.slice(0);
      const [result] = await Promise.all([
        sonic.loadSample(bufnum, arrayBuffer),
        analyzeSample(name, analysisBuffer, ctx),
      ]);
      bufferInfo[bufnum] = { numFrames: result.numFrames, numChannels: result.numChannels, sampleRate: result.sampleRate };
      sampleBufnums[name] = bufnum;
      return bufnum;
    }

    // Fall back to built-in samples — fetch raw bytes so we can analyze
    let arrayBuffer;
    try {
      const resp = await fetch('samples/' + name + '.flac');
      if (resp.ok) arrayBuffer = await resp.arrayBuffer();
    } catch {}

    let result;
    if (arrayBuffer) {
      const analysisBuffer = arrayBuffer.slice(0); // clone before loadSample detaches it
      [result] = await Promise.all([
        sonic.loadSample(bufnum, arrayBuffer),
        analyzeSample(name, analysisBuffer, ctx),
      ]);
    } else {
      // Fetch failed — fall back to letting SuperSonic handle it
      result = await sonic.loadSample(bufnum, name + '.flac');
    }
    bufferInfo[bufnum] = { numFrames: result.numFrames, numChannels: result.numChannels, sampleRate: result.sampleRate };
    // If analysis didn't run, compute duration from loadSample metadata
    if (!sampleMeta[name] && result.numFrames > 0 && result.sampleRate > 0) {
      const duration = result.numFrames / result.sampleRate;
      sampleMeta[name] = { duration, onsets: [0.0] };
      console.log('[tau-cam] Duration from metadata:', name, duration.toFixed(3) + 's');
    }
    sampleBufnums[name] = bufnum;
    return bufnum;
  }

  // --- MIDI connect (triggered by UI) ---
  window._midiEnabled = false;
  window._midiDevices = [];

  window._enableMidi = async function() {
    try {
      console.log('[tau-cam] Requesting MIDI access...');
      if (!navigator.requestMIDIAccess) {
        console.warn('[tau-cam] Web MIDI API not available in this browser');
        return;
      }
      const midi = await navigator.requestMIDIAccess();
      console.log('[tau-cam] MIDI access granted, inputs:', midi.inputs.size);
      window._midiEnabled = true;

      function connectMidiInput(input) {
        if (window._midiDevices.some(d => d.id === input.id)) return;
        const devIdx = window._midiDevices.length;
        const safeName = input.name.replace(/[^a-zA-Z0-9_-]/g, '_');
        console.log('[tau-cam] MIDI connected:', input.name, '→', safeName + '_' + devIdx);
        window._midiDevices.push({ id: input.id, name: input.name, safeName, devIdx });
        if (window._updateMidiUI) window._updateMidiUI();
        input.onmidimessage = (event) => {
          const [status, data1, data2] = event.data;
          const beat = ctx.currentTime * BPM / 60;
          const ch = (status & 0x0F) + 1; // 1-indexed like Sonic Pi
          const dev = safeName + '_' + devIdx;
          // Paths: /midi:<device>:<channel>/<type>
          // Also fire generic /midi/*/<type> for easy matching
          const prefix = '/midi/' + dev + '/' + ch;
          const msg = status & 0xF0;
          if (msg === 0x90 && data2 > 0) {
            inboxRing.storeInt(prefix + '/note_on', beat, 0, data1);
            inboxRing.storeInt('/midi/note_on', beat, 0, data1);
          } else if (msg === 0x80 || (msg === 0x90 && data2 === 0)) {
            inboxRing.storeInt(prefix + '/note_off', beat, 0, data1);
            inboxRing.storeInt('/midi/note_off', beat, 0, data1);
          } else if (msg === 0xB0) {
            inboxRing.storeInt(prefix + '/cc/' + data1, beat, 0, data2);
            inboxRing.storeInt('/midi/cc/' + data1, beat, 0, data2);
          } else if (msg === 0xD0) {
            inboxRing.storeInt(prefix + '/channel_pressure', beat, 0, data1);
          } else if (msg === 0xE0) {
            const bend = (data2 << 7) | data1;
            inboxRing.storeInt(prefix + '/pitch_bend', beat, 0, bend);
            inboxRing.storeInt('/midi/pitch_bend', beat, 0, bend);
          } else if (msg === 0xC0) {
            inboxRing.storeInt(prefix + '/program_change', beat, 0, data1);
          }
        };
      }

      for (const input of midi.inputs.values()) {
        connectMidiInput(input);
      }
      midi.onstatechange = (e) => {
        if (e.port.type === 'input' && e.port.state === 'connected') {
          connectMidiInput(e.port);
        }
        if (e.port.type === 'input' && e.port.state === 'disconnected') {
          window._midiDevices = window._midiDevices.filter(d => d.id !== e.port.id);
          if (window._updateMidiUI) window._updateMidiUI();
        }
      };

      if (window._updateMidiUI) window._updateMidiUI();
    } catch (e) {
      console.warn('[tau-cam] MIDI not available:', e.message);
      if (window._updateMidiUI) window._updateMidiUI();
    }
  };


  return {
    ctx, node, sonic,
    inboxRing, inboxSAB,
    metricsView, BPM,
    sampleBufnums, bufferInfo,
    ensureSampleLoaded,
  };
}

// --- Run ---
// Resolve a packed sample name: "dir|filter1|filter2|idx" → concrete sample path
function resolveSampleName(packed) {
  if (!packed.includes('|')) return packed; // simple name, no filters
  const parts = packed.split('|');
  // Separate path filters (contain /), string filters (prefixed s:), and index.
  // Quoted strings from source are marked "s:text" by the compiler to prevent
  // numeric strings like "120" from being misread as an index.
  const pathFilters = [];
  const nameFilters = [];
  let idx = 0;
  for (const part of parts) {
    if (part.startsWith('s:')) {
      // Explicitly a string filter (was quoted in source)
      const text = part.slice(2);
      if (text.includes('/')) pathFilters.push(text.toLowerCase());
      else nameFilters.push(text.toLowerCase());
    } else {
      const n = parseInt(part);
      if (!isNaN(n) && String(n) === part) {
        idx = n;
      } else if (part.includes('/')) {
        pathFilters.push(part.toLowerCase());
      } else {
        nameFilters.push(part.toLowerCase());
      }
    }
  }
  // Path filters narrow directory space, string filters narrow filenames
  let candidates = Object.keys(userSampleIndex).filter(p => {
    const lp = p.toLowerCase();
    const lastSlash = lp.lastIndexOf('/');
    const dirPart = lastSlash >= 0 ? lp.substring(0, lastSlash) : '';
    const filePart = lastSlash >= 0 ? lp.substring(lastSlash + 1) : lp;
    // All path filters must match somewhere in the directory portion
    if (!pathFilters.every(f => dirPart.includes(f))) return false;
    // All string filters must match in the filename
    if (!nameFilters.every(f => filePart.includes(f))) return false;
    return true;
  });
  candidates.sort();
  if (candidates.length === 0) {
    console.warn('[tau-cam] No samples found — paths:', pathFilters, 'names:', nameFilters);
    return null;
  }
  const picked = candidates[idx % candidates.length];
  console.log('[tau-cam] Sample filter → ', picked, '(' + candidates.length + ' matches)');
  return picked;
}

async function handleRun(source, { skipFlashMap = false, skipEditorFlash = false, precompiled = null, verb = 'restart' } = {}) {
  if (!audio) return;
  document.body.classList.add('audio-running');

  // 1. Compile (or use precompiled result)
  const result = precompiled || await compileSource(source);

  if (!result.bytecode || result.bytecode.length === 0 || (result.errors && result.errors.length > 0)) {
    // Bug B fix: surface compile errors via the runtime error pane
    // (the same pink panel used for runtime VM errors), so users can
    // see what's wrong instead of getting silent failure.
    clearRuntimeErrors();
    if (result.errors && result.errors.length > 0) {
      for (const e of result.errors) {
        const line = (e.line !== undefined) ? `Line ${e.line}: ` : '';
        addRuntimeError(0, `${line}${e.text || e.message || String(e)}`);
      }
    } else {
      addRuntimeError(0, 'Compile produced no bytecode (no errors reported)');
    }
    return;
  }

  // Snapshot the statement map for this compilation — flashes will use it
  // so runtime IDs always match the bytecode that's actually executing.
  if (!skipFlashMap && window.editor?.setFlashMap) {
    window.editor.setFlashMap(result.statementMap);
  }

  // Whole-editor flash on Run (skip for snippet runs)
  const flashMode = window._runFlashMode || 'full';
  if (skipEditorFlash) { /* snippet run — no main editor flash */ }
  else if (flashMode === 'full') {
    const flashEl = document.getElementById('run-flash');
    if (flashEl) {
      flashEl.classList.remove('active');
      void flashEl.offsetWidth;
      flashEl.classList.add('active');
      flashEl.addEventListener('animationend', () => flashEl.classList.remove('active'), { once: true });
    }
  } else if (flashMode === 'text') {
    const cmEditor = document.querySelector('.cm-editor');
    if (cmEditor) {
      cmEditor.classList.add('run-flash');
      setTimeout(() => cmEditor.classList.remove('run-flash'), 250);
    }
  }

  // 2. Load samples (resolve filter expressions like "dir|filter|idx")
  if (result.usedSamples && result.usedSamples.length > 0) {
    const samplesToLoad = result.usedSamples.filter(s => !audio.sampleBufnums[s.includes('|') ? s.split('|')[0] : s]);
    if (samplesToLoad.length > 0 && !audio._hasBooted) {
      showLoadingLog();
    }
    for (const s of result.usedSamples) {
      if (s.includes('|')) {
        // Packed filter: resolve to concrete name, load it,
        // register bufnum under the dir part (what bytecode references)
        const dirPart = s.split('|')[0];
        const resolved = resolveSampleName(s);
        if (!resolved) continue; // no matching samples — skip
        console.log('[tau-cam] Filter resolved:', s, '→', resolved);
        const bufnum = await audio.ensureSampleLoaded(resolved);
        audio.sampleBufnums[dirPart] = bufnum;
        // Store resolved name so activity log can show the actual filename
        audio.resolvedSampleNames = audio.resolvedSampleNames || {};
        audio.resolvedSampleNames[dirPart] = resolved;
      } else {
        await audio.ensureSampleLoaded(s);
      }
    }
  }

  // 3. Load missing synthdefs
  if (result.usedSynths && audio.sonic) {
    const toLoad = result.usedSynths.filter(s => !audio.sonic.loadedSynthDefs.has(s));
    for (const s of toLoad) {
      await audio.sonic.loadSynthDef(s);
    }
  }

  hideLoadingLog();

  // 4. Load new VM (SpawnNamed will hot-swap existing live loops)

  // 5. Cancel any pending stop fade so it doesn't kill new synths
  cancelPendingStop();
  const vol = parseFloat(localStorage.getItem('tau-pref:volume') || '0.8');
  try { audio.sonic.send('/n_set', window._FX_MIXER, 'amp_slide', 0); } catch (_) {}
  try { audio.sonic.send('/n_set', window._FX_MIXER, 'amp', vol); } catch (_) {}

  // Write bufnum mappings + channel counts + load VM
  const beat = audio.ctx.currentTime * audio.BPM / 60;
  for (const name in audio.sampleBufnums) {
    const bufnum = audio.sampleBufnums[name];
    audio.inboxRing.storeInt('/buf/' + name, beat, 0, bufnum);
    const info = audio.bufferInfo[bufnum];
    audio.inboxRing.storeInt('/buf/' + name + '/ch', beat, 0, info ? info.numChannels : 2);
    // Write resolved name for filter-resolved samples so activity log shows actual filename
    const resolvedName = audio.resolvedSampleNames && audio.resolvedSampleNames[name];
    if (resolvedName) {
      const nameBytes = new TextEncoder().encode(resolvedName);
      audio.inboxRing.storeBlob('/buf/' + name + '/name', beat, 0, nameBytes, nameBytes.length);
    }
    // Write sample analysis metadata (duration + onsets)
    const meta = sampleMeta[name];
    if (meta) {
      console.log('[tau-cam] Writing sample meta:', name, 'dur:', meta.duration, 'onsets:', meta.onsets.length);
      audio.inboxRing.storeFloat('/buf/' + name + '/dur', beat, 0, meta.duration);
      // Store onset count as int
      audio.inboxRing.storeInt('/buf/' + name + '/onset_count', beat, 0, meta.onsets.length);
      // Store onset pairs as blob: [f32 start0, f32 finish0, f32 start1, f32 finish1, ...]
      const numOnsets = meta.onsets.length;
      const blobLen = numOnsets * 8; // 2 f32s per onset
      const blobBuf = new Uint8Array(blobLen);
      const blobView = new DataView(blobBuf.buffer);
      for (let i = 0; i < numOnsets; i++) {
        const start = meta.onsets[i];
        const finish = (i + 1 < numOnsets) ? meta.onsets[i + 1] : 1.0;
        blobView.setFloat32(i * 8, start, true);     // LE
        blobView.setFloat32(i * 8 + 4, finish, true); // LE
      }
      audio.inboxRing.storeBlob('/buf/' + name + '/onsets', beat, 0, blobBuf, blobLen);
    } else {
      console.warn('[tau-cam] No sample meta for:', name);
    }
  }

  // Tiny lookahead — enough for the worklet's next quantum to pick it up
  // but not enough to be perceptible on a drum-pad trigger. At 60bpm,
  // 0.01 beats = 10ms; at 120bpm, 5ms.
  const startBeat = beat + 0.01;
  // restart vs start: jam-mode 'run' (verb='start') wants to LAYER a new
  // VM into the existing session without tearing down the previous run.
  // Default ('restart') is the editor's main Run button: atomic fade +
  // replace in one /supersonic/osc bundle.
  if (verb === 'start') {
    audio.inboxRing.startRun('core/editor', result.bytecode, startBeat);
  } else {
    audio.inboxRing.restartRun('core/editor', result.bytecode, startBeat);
  }

  console.log('[tau-cam] run restarted at beat', beat.toFixed(3), '— bytecode:', result.bytecode.length, 'bytes');

  // Auto-attach scopes to live loops — retry a few times as loops initialize
  for (let d = 500; d <= 3000; d += 500) {
    setTimeout(() => { try { window._scopeLoops(); } catch(_) {} }, d);
  }

  return result;
}

// --- Stop ---
let _stopping = false;
let _stopTimer = null;
let _stopRestoreTimer = null;
const FADE_MS = 80;

function cancelPendingStop() {
  if (_stopTimer) { clearTimeout(_stopTimer); _stopTimer = null; }
  if (_stopRestoreTimer) { clearTimeout(_stopRestoreTimer); _stopRestoreTimer = null; }
  _stopping = false;
}

function handleStop() {
  if (!audio || _stopping) return;
  _stopping = true;
  document.body.classList.remove('audio-running');
  window.editor?.stopLiveLoopBars?.();
  if (_synthUuidToStmt.size > 0) {
    console.warn(`[tau-cam] stop: ${_synthUuidToStmt.size} orphaned synth UUID(s) — /n_end missed?`);
  }
  _synthUuidToStmt.clear();

  const vol = parseFloat(localStorage.getItem('tau-pref:volume') || '0.8');

  // Fade master mixer to silence over FADE_MS for a graceful audible stop.
  try { audio.sonic.send('/n_set', window._FX_MIXER, 'amp_slide', FADE_MS / 1000); } catch (_) {}
  try { audio.sonic.send('/n_set', window._FX_MIXER, 'amp', 0); } catch (_) {}

  _stopTimer = setTimeout(() => {
    _stopTimer = null;
    // End the current run via tau-state (halts VMs, emits /g_freeAll +
    // /n_free for the run's per-run synth group). No /stop — keeps the
    // engine live so the next Run can start immediately.
    try { audio.inboxRing.endRun('core/editor'); } catch (_) {}

    _stopRestoreTimer = setTimeout(() => {
      _stopRestoreTimer = null;
      try { audio.sonic.send('/n_set', window._FX_MIXER, 'amp_slide', 0); } catch (_) {}
      try { audio.sonic.send('/n_set', window._FX_MIXER, 'amp', vol); } catch (_) {}
      _stopping = false;
    }, 20);

    console.log('[tau-cam] Stopped');
  }, FADE_MS);
}

// --- Boot + Run on first click ---
window.ensureBooted = ensureBooted;
async function ensureBooted() {
  if (audio) return;
  if (booting) return;
  booting = true;

  const [audioResult] = await Promise.all([
    bootAudio(),
    ...(!compileWorker ? [initCompileWorker()] : []),
  ]);

  audio = audioResult;
  audio._hasBooted = true;
  booting = false;
}

// --- Keyboard shortcuts ---
document.addEventListener('keydown', (e) => {
  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && btnRun && !btnRun.disabled) {
    e.preventDefault();
    btnRun.click();
  }
  if ((e.ctrlKey || e.metaKey) && e.key === '.' && btnStop) {
    e.preventDefault();
    btnStop.click();
  }
  if (e.altKey && e.key >= '0' && e.key <= '7' && window.editor?.switchBuffer) {
    e.preventDefault();
    window.editor.switchBuffer(parseInt(e.key, 10));
  }
});

// --- Button handlers ---
if (btnRun) {
  async function doRun(verb = 'start') {
    if (btnRun.disabled) return;
    // First-visit UX: pressing Run is a commitment to the session —
    // dismiss the welcome CTA so it doesn't linger over the running app.
    if (window._dismissOnboarding) window._dismissOnboarding();
    try {
      btnRun.disabled = true;
      await ensureBooted();
      await window.editor.format();

      // Clear output panes on new run
      clearStdout();
      clearRuntimeErrors();
      clearOscLog();
      clearCueLog();

      const source = window.editor.getSource();
      const MAX_SOURCE_BYTES = 131072; // 128KB
      if (new TextEncoder().encode(source).length > MAX_SOURCE_BYTES) {
        addRuntimeError(0, 'Source code exceeds 128KB limit (' + Math.round(new TextEncoder().encode(source).length / 1024) + 'KB). Please reduce the size of your code.');
        return;
      }
      await handleRun(source, { verb });
    } catch (err) {
      console.error('[tau-cam] Run error:', err);
    } finally {
      hideLoadingLog();
      btnRun.disabled = false;
    }
  }
  btnRun.onclick = doRun;
  window._triggerRun = doRun;

  // ── Jam-mode fast reset path ────────────────────────────────────────
  // Goal: <5ms from touch to sound. Normal doRun() awaits compile
  // (~15ms), format (tens of ms), and DOM clears — all too slow for
  // drum-pad triggers. Precompile bytecode per-buffer on edit
  // (debounced) and on reset fire `/run/restart` synchronously from
  // the event handler, deferring cosmetic DOM/flash work.
  //
  // Per-buffer cache: _preCompiled[bufIdx] = { source, result }. Keyed
  // by buffer index because switchBuffer() replaces the editor state
  // without firing a docChanged, so a single-slot cache would play the
  // previous buffer's bytecode after a tab switch.
  const _preCompiled = new Map();
  const _preCompileTimers = new Map();
  const _preCompileInFlight = new Set();

  async function runPrecompileFor(idx) {
    if (_preCompileInFlight.has(idx)) return;
    _preCompileInFlight.add(idx);
    try {
      const source = window.editor?.getSource?.() ?? '';
      if (window.editor?.activeBuffer !== idx) return; // buffer changed mid-compile
      if (!source) { _preCompiled.delete(idx); return; }
      const result = await compileSource(source);
      // Re-check still the active buffer + source hasn't mutated since.
      if (window.editor?.activeBuffer !== idx) return;
      if (result?.bytecode?.length && !(result.errors && result.errors.length)) {
        _preCompiled.set(idx, { source, result });
      } else {
        _preCompiled.delete(idx);
      }
    } catch (_) {
      _preCompiled.delete(idx);
    } finally {
      _preCompileInFlight.delete(idx);
    }
  }

  // Hook called from editor.js on every docChanged transaction.
  window._onSourceDirty = function() {
    const idx = window.editor?.activeBuffer ?? 0;
    _preCompiled.delete(idx); // invalidate synchronously
    const t = _preCompileTimers.get(idx);
    if (t) clearTimeout(t);
    _preCompileTimers.set(idx, setTimeout(() => {
      _preCompileTimers.delete(idx);
      runPrecompileFor(idx);
    }, 80));
  };

  // Prime the active buffer's cache on boot so the first reset tap is fast.
  setTimeout(() => runPrecompileFor(window.editor?.activeBuffer ?? 0), 500);

  // Internal: fire a precompiled bytecode at the current beat using the
  // chosen tau-state OSC verb. `verb` is either 'restart' (atomic
  // end-current + start-new — replaces the editor session in one tick)
  // or 'start' (just dispatches a new VM into the existing session,
  // layering on top of whatever's already running). Returns true on
  // success, false if the cold fallback to doRun() should be used.
  function _fireFastPath(verb) {
    const idx = window.editor?.activeBuffer ?? 0;
    const pre = _preCompiled.get(idx);
    // Cold fallback if not booted, no cache, OR source has diverged from
    // the cached version (e.g. tab switch before precompile caught up).
    // Pass the verb so the cold path also honours layer vs replace —
    // otherwise jam-mode 'run' silently behaves like 'exec' until the
    // precompile cache catches up.
    if (!audio || !pre || pre.source !== window.editor?.getSource?.()) {
      doRun(verb);
      return;
    }
    // Hot path: zero awaits, zero DOM work, single OSC bundle.
    const beat = audio.ctx.currentTime * audio.BPM / 60;
    try {
      if (verb === 'start') {
        audio.inboxRing.startRun('core/editor', pre.result.bytecode, beat + 0.01);
      } else {
        audio.inboxRing.restartRun('core/editor', pre.result.bytecode, beat + 0.01);
      }
    } catch (_) {
      doRun();
      return;
    }
    // Defer cosmetic work until after the audio quantum has picked up the bundle.
    queueMicrotask(() => {
      if (window._dismissOnboarding) window._dismissOnboarding();
      document.body.classList.add('audio-running');
      if (window.editor?.setFlashMap) window.editor.setFlashMap(pre.result.statementMap);
      clearStdout();
      clearRuntimeErrors();
      clearOscLog();
      clearCueLog();
    });
    // Re-attach per-loop scopes after the new run boots. Same retry
    // ladder as handleRun's cold path — live_loop VMs need a few ticks
    // to spawn + acquire a loop_bus before _scopeLoops can re-point
    // their scope nodes. Without this, exec-mode taps strand the
    // scope visualisers on stale buses.
    for (let d = 200; d <= 2000; d += 400) {
      setTimeout(() => { try { window._scopeLoops?.(); } catch(_) {} }, d);
    }
  }

  // Jam-mode 'exec' button: end-and-replace the editor session.
  window._triggerStopRun = function() { _fireFastPath('restart'); };

  // Jam-mode 'run' button: dispatch a NEW VM into the editor session
  // without ending what's already there — each tap layers another
  // execution of the buffer on top of any currently-playing ones.
  window._triggerLayerRun = function() { _fireFastPath('start'); };
}

if (btnStop) {
  function doStop() {
    handleStop();
    window.editor.setReadOnly(false);
    // First-visit UX: stopping is a decisive action; dismiss the welcome
    // CTA so it doesn't linger awkwardly over a stopped session.
    if (window._dismissOnboarding) window._dismissOnboarding();
  }
  btnStop.onclick = doStop;
}

// Toolbar: visual clunk on press, instant fire for run/stop only
const toolbar = document.getElementById('toolbar');
if (toolbar) {
  const _instantBtns = new Set([btnRun, btnStop].filter(Boolean));
  let _pointerFired = null;
  toolbar.addEventListener('pointerdown', (e) => {
    const btn = e.target.closest('button');
    if (!btn || btn.disabled) return;
    // Clear any stale suppression from a previous tap
    _pointerFired = null;
    // Clunk: shift down on press, snap back on release
    btn.style.transition = 'none';
    btn.style.transform = 'translate(1px, 1px)';
    const up = () => {
      btn.style.transition = 'transform 0.1s ease-out';
      btn.style.transform = '';
      btn.removeEventListener('pointerup', up);
      btn.removeEventListener('pointerleave', up);
    };
    btn.addEventListener('pointerup', up);
    btn.addEventListener('pointerleave', up);
    // Fire run/stop instantly on pointerdown; others use normal click
    if (_instantBtns.has(btn)) {
      _pointerFired = btn;
      btn.onclick?.(e);
    }
  });
  // Suppress the natural click that follows a pointerdown-fired button
  toolbar.addEventListener('click', (e) => {
    if (_pointerFired) {
      const btn = e.target.closest('button');
      if (btn === _pointerFired) {
        _pointerFired = null;
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    }
  }, true);
}

// Expose for help panel snippet buttons
window._snippetStmtMap = null;

window._runSnippet = async (source) => {
  try {
    await ensureBooted();
    clearStdout();
    clearRuntimeErrors();
    clearOscLog();

    // Compile with high ID offset so snippet IDs never collide with main editor IDs
    const result = await compileSource(source, { stmtIdStart: 1_000_000 });
    if (result && result.statementMap) {
      const map = new Map();
      for (const entry of result.statementMap) {
        map.set(entry.id, { start: entry.start, end: entry.end });
      }
      window._snippetStmtMap = map;
    }

    // Pass precompiled result so handleRun doesn't recompile (which would produce different IDs)
    await handleRun(source, { skipFlashMap: true, skipEditorFlash: true, precompiled: result });
  } catch (err) {
    console.error('[tau-cam] Snippet run error:', err);
  }
};
window._stopSnippet = () => {
  handleStop();
  window._snippetStmtMap = null;
  window.editor.setReadOnly(false);
};

// --- Font size buttons ---
// Initial --editor-font-size is set pre-render from localStorage by an inline
// <script> in <head>. Read it back here as the source of truth.
let editorFontSize;
(function() {
  const cssVal = getComputedStyle(document.documentElement).getPropertyValue('--editor-font-size').trim();
  const parsed = parseInt(cssVal, 10);
  editorFontSize = (parsed >= 6 && parsed <= 32) ? parsed
    : (window.matchMedia('(max-width: 520px)').matches ? 9 : 14);
})();
const btnSizeUp = document.getElementById('btn-size-up');
const btnSizeDown = document.getElementById('btn-size-down');

function setEditorFontSize(size) {
  editorFontSize = Math.max(6, Math.min(32, size));
  document.documentElement.style.setProperty('--editor-font-size', editorFontSize + 'px');
  if (window.editor?.view) window.editor.view.requestMeasure();
  try { localStorage.setItem('tau-pref:font-size', editorFontSize); } catch(e) {}
}

// Pinch-to-zoom changes editor font size
{
  const editorMount = document.getElementById('editor-mount');
  if (editorMount) {
    let pinchStartDist = 0;
    let pinchStartSize = 0;
    editorMount.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        const dx = e.touches[1].clientX - e.touches[0].clientX;
        const dy = e.touches[1].clientY - e.touches[0].clientY;
        pinchStartDist = Math.hypot(dx, dy);
        pinchStartSize = editorFontSize;
      }
    }, { passive: true });
    editorMount.addEventListener('touchmove', (e) => {
      if (e.touches.length === 2 && pinchStartDist > 0) {
        e.preventDefault();
        const dx = e.touches[1].clientX - e.touches[0].clientX;
        const dy = e.touches[1].clientY - e.touches[0].clientY;
        const dist = Math.hypot(dx, dy);
        const scale = dist / pinchStartDist;
        setEditorFontSize(Math.round(pinchStartSize * scale));
      }
    }, { passive: false });
    editorMount.addEventListener('touchend', () => { pinchStartDist = 0; }, { passive: true });
  }
}

if (btnSizeUp) {
  btnSizeUp.addEventListener('pointerdown', (e) => { e.preventDefault(); setEditorFontSize(editorFontSize + 2); });
}
if (btnSizeDown) {
  btnSizeDown.addEventListener('pointerdown', (e) => { e.preventDefault(); setEditorFontSize(editorFontSize - 2); });
}

// --- Share button + popup ---
const btnShare = document.getElementById('btn-share');
const shareOverlay = document.getElementById('share-overlay');
const shareUrlText = document.getElementById('share-url-text');
const shareStats = document.getElementById('share-stats');
const shareBtnCopy = document.getElementById('share-btn-copy');
const shareBtnClose = document.getElementById('share-btn-close');
const shareTitleInput = document.getElementById('share-title');
let _shareHashData = ''; // raw compressed data, updated when share popup opens

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
}

function buildShareUrl(hash, title) {
  const base = window.location.origin + window.location.pathname;
  const slug = slugify(title || '');
  if (slug) return base + '#s=--' + slug + '--' + hash;
  return base + '#s=' + hash;
}

function closeSharePopup() {
  if (shareOverlay) shareOverlay.classList.remove('visible');
}

if (shareOverlay) {
  shareOverlay.addEventListener('click', (e) => {
    if (e.target === shareOverlay) closeSharePopup();
  });
}
if (shareBtnClose) shareBtnClose.onclick = closeSharePopup;
if (shareTitleInput) {
  shareTitleInput.oninput = () => {
    if (!_shareHashData) return;
    const url = buildShareUrl(_shareHashData, shareTitleInput.value);
    if (shareUrlText) shareUrlText.textContent = url;
  };
}
function copyShareUrl() {
  const url = shareUrlText?.textContent;
  if (url) {
    navigator.clipboard.writeText(url);
    if (shareBtnCopy) {
      shareBtnCopy.textContent = 'Copied!';
      setTimeout(() => { shareBtnCopy.textContent = 'Copy link'; }, 1500);
    }
  }
}
if (shareBtnCopy) shareBtnCopy.onclick = copyShareUrl;
if (shareUrlText) shareUrlText.onclick = copyShareUrl;

if (btnShare) {
  btnShare.onclick = async () => {
    const source = window.editor.getSource();
    if (!source.trim()) return;
    try {
      btnShare.disabled = true;
      const leaves = window.editor.getLastResult()?.leaves;
      _shareHashData = await compressToHash(source, leaves);
      if (shareTitleInput) shareTitleInput.value = '';
      const url = buildShareUrl(_shareHashData, '');
      await navigator.clipboard.writeText(url);

      // Show popup
      if (shareUrlText) shareUrlText.textContent = url;
      if (shareStats) {
        const ratio = Math.round(_shareHashData.length / source.length * 100);
        shareStats.textContent = source.length + ' chars \u2192 ' + _shareHashData.length + ' compressed (' + ratio + '% of original)';
      }
      if (shareBtnCopy) shareBtnCopy.textContent = 'Copy link';
      if (shareOverlay) shareOverlay.classList.add('visible');
    } catch (err) {
      console.error('[sonic-tau] Share error:', err);
    } finally {
      btnShare.disabled = false;
    }
  };
}

// --- Load shared code from URL hash ---
function loadFromHash() {
  const hash = window.location.hash;
  if (!hash.startsWith('#s=')) return;
  // Strip optional title: #s=--my-title--<data> → <data>
  let data = hash.slice(3);
  if (data.startsWith('--')) {
    const endIdx = data.indexOf('--', 2);
    if (endIdx > 2) data = data.slice(endIdx + 2);
  }
  decompressFromHash(data).then((source) => {
    if (window.editor?.view && source) {
      const view = window.editor.view;
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: source },
      });
      // Clear the hash so refreshing doesn't re-load over edits
      history.replaceState(null, '', window.location.pathname);
    }
  }).catch((err) => {
    console.error('[sonic-tau] Failed to load shared code:', err);
  });
}

// Load on startup and on hash change (e.g. clicking a share link while already on the page)
loadFromHash();
window.addEventListener('hashchange', loadFromHash);

// --- Virtual keyboard + FX pad ---
// Apply inputmode now that CodeMirror has mounted (inline script ran before cm-content existed)
{
  const cmContent = document.querySelector('.cm-content');
  if (cmContent && window._kbdMode === 'custom') {
    cmContent.setAttribute('inputmode', 'none');
  }
}

const kbdEl = document.getElementById('code-keyboard');
if (kbdEl) {
  kbdEl.classList.add('code-kbd');
  let _kbdCapsLock = false;
  let _kbdShiftMode = false;
  const kbd = new Keyboard('.code-kbd', {
    layout: {
      default: [
        'q w e r t y u i o p',
        '{caps} a s d f g h j k l @',
        '{shift} z x c v b n m {tab} {bksp}',
        '{shift2} : . = [ ] ( ) {up} {enter}',
        '{hide} _ {space} {left} {right} {down} {enter2}',
      ],
      caps: [
        'Q W E R T Y U I O P',
        '{caps} A S D F G H J K L @',
        '{shift} Z X C V B N M {tab} {bksp}',
        '{shift2} : . = [ ] ( ) {up} {enter}',
        '{hide} _ {space} {left} {right} {down} {enter2}',
      ],
      shift: [
        '1 2 3 4 5 6 7 8 9 0',
        '{caps} + - * / % & | ^ ~ ! :',
        '{shift} " \' # ? , . {undo} {tab} {bksp}',
        '{shift2} { } < > {selectall} {copy} {paste} {up} {enter}',
        '{hide} _ {space} {left} {right} {down} {enter2}',
      ],
    },
    display: {
      '{bksp}': '⌫',
      '{enter}': '↵',
      '{shift}': '123',
      '{shift2}': '123',
      '{enter2}': '↵',
      '{caps}': '⇪\uFE0E',
      '{space}': ' ',
      '{hide}': '⌄',
      '{r3end}': ' ',
      '{undo}': '↶',
      '{selectall}': '⬚',
      '{cut}': '✂\uFE0E',
      '{copy}': '⧉',
      '{paste}': '⎘',
      '{left}': '←',
      '{up}': '↑',
      '{down}': '↓',
      '{right}': '→',
      '{tab}': '⇥',
    },
    buttonTheme: [{
      class: 'kbd-letter',
      buttons: 'q w e r t y u i o p a s d f g h j k l z x c v b n m Q W E R T Y U I O P A S D F G H J K L Z X C V B N M',
    }],
    layoutName: 'default',
    preventMouseDownDefault: true,
    onRender: (kb) => {
      const kbEl = document.getElementById('code-keyboard');
      if (!kbEl) return;
      const isShift = kb.options.layoutName === 'shift';
      const rows = Array.from(kbEl.querySelectorAll('.hg-row'));
      // Locate rows by the special key they contain — robust to row-count changes.
      const rowContaining = (k) => rows.find(r => r.querySelector('.hg-button[data-skbtn="' + k + '"]'));
      const capsRow = rowContaining('{caps}');
      const shiftRow = rowContaining('{shift}');
      const altRow = rowContaining('{shift2}');
      const bottomRow = rowContaining('{space}');

      // Caps row: caps key half-width left, last key (:/{r3end}) half-width right
      if (capsRow) {
        const capsBtn = capsRow.querySelector('.hg-button[data-skbtn="{caps}"]');
        if (capsBtn) {
          capsBtn.classList.toggle('caps-locked', _kbdCapsLock);
          capsBtn.style.setProperty('flex', '0.5 1 0', 'important');
          capsBtn.style.setProperty('min-width', '0', 'important');
        }
        const lastBtn = capsRow.querySelector('.hg-button:last-child');
        if (lastBtn) {
          lastBtn.style.setProperty('flex', '0.5 1 0', 'important');
          lastBtn.style.setProperty('min-width', '0', 'important');
          if (lastBtn.getAttribute('data-skbtn') === '{r3end}') {
            lastBtn.style.setProperty('visibility', 'hidden');
            lastBtn.style.setProperty('pointer-events', 'none');
          }
        }
      }

      // Shift row: {shift} is the WIDE top of the stepped fn pillar (flex 1.5).
      // Shift2 in the alt row below is flex 1, so the pillar tapers: wider at
      // the top, narrower at the bottom. Both rows sum to flex 11 so the
      // pseudo-element ratio is a clean 1/1.5 = 66.67%.
      //
      // Row-3 corner radii: bottom-LEFT of {shift} is 0 (abuts the pseudo
      // that paints the narrower lower half). Bottom-RIGHT keeps its 4px
      // radius — that edge is the step in the gavel silhouette.
      if (shiftRow) {
        const btns = shiftRow.querySelectorAll('.hg-button');
        const last = btns.length - 1;
        btns.forEach((btn, i) => {
          const key = btn.getAttribute('data-skbtn');
          if (i === 0) {
            btn.style.setProperty('flex', '1.5 1 0', 'important');
            btn.style.setProperty('min-width', '0', 'important');
            btn.style.setProperty('font-size', '14px', 'important');
            btn.style.setProperty('border-bottom-left-radius', '0', 'important');
          } else if (i === last) {
            btn.style.setProperty('flex', '1.5 1 0', 'important');
            btn.style.setProperty('font-size', '18px', 'important');
          }
        });
      }

      // Stepped fn pillar and stepped return pillar are both painted via two
      // REAL visible buttons per pillar ({shift}+{shift2}, {enter}+{enter2}).
      // The lower half has margin-top: -3px to abut the upper half through the
      // row-gap (see index.html for those rules). Widths differ between halves
      // to produce the gavel-shape "step" the user requested.

      // Alt row (above space bar): {shift2}(1) + NEW(1) + {tab}(1) + brackets(5)
      // + {up}(1.5) + {enter}(1.5) = 11 — matches shift row total.
      if (altRow) {
        altRow.querySelectorAll('.hg-button').forEach(btn => {
          const key = btn.getAttribute('data-skbtn');
          if (key === '{shift2}') {
            btn.style.setProperty('flex', '1 1 0', 'important');
            btn.style.setProperty('min-width', '0', 'important');
            btn.style.setProperty('font-size', '14px', 'important');
            btn.style.setProperty('border-top-left-radius', '0', 'important');
            btn.style.setProperty('border-top-right-radius', '0', 'important');
          } else if (key === '{up}') {
            btn.style.setProperty('flex', '1.5 1 0', 'important');
            btn.style.setProperty('min-width', '0', 'important');
            btn.style.setProperty('font-size', '20px', 'important');
          } else if (key === '{enter}') {
            btn.style.setProperty('flex', '1.5 1 0', 'important');
            btn.style.setProperty('font-size', '18px', 'important');
            btn.style.setProperty('border-bottom-left-radius', '0', 'important');
            btn.style.setProperty('border-bottom-right-radius', '0', 'important');
          }
        });
      }

      // Bottom row: {hide}(1) + ((0.5) + {space}(3) + )(0.5) + three arrows(1.5 each)
      // + {enter2}(1.5) = 11 — matches alt row so the pillars' columns align.
      if (bottomRow) {
        bottomRow.querySelectorAll('.hg-button').forEach(btn => {
          const key = btn.getAttribute('data-skbtn');
          if (key === '{hide}') {
            btn.style.setProperty('flex', '1 1 0', 'important');
            btn.style.setProperty('min-width', '0', 'important');
          } else if (key === '{space}') {
            btn.style.setProperty('flex', '3 1 0', 'important');
          } else if (key === '{left}' || key === '{right}' || key === '{down}') {
            btn.style.setProperty('flex', '1.5 1 0', 'important');
            btn.style.setProperty('min-width', '0', 'important');
            btn.style.setProperty('font-size', '20px', 'important');
          } else if (key === '{enter2}') {
            btn.style.setProperty('flex', '1.5 1 0', 'important');
            btn.style.setProperty('font-size', '18px', 'important');
            btn.style.setProperty('border-top-left-radius', '0', 'important');
            btn.style.setProperty('border-top-right-radius', '0', 'important');
          }
        });
      }
      kbEl.classList.toggle('caps-locked', _kbdCapsLock);
    },
    onKeyPress: (button) => {
      if (button === '{hide}') {
        if (window._closeKbdDock) window._closeKbdDock();
        return;
      }
      // {enter2} is the bottom half of the tall return pillar — same action.
      if (button === '{enter2}') button = '{enter}';
      const view = window.editor?.view;
      if (!view) return;

      if (button === '{enter}') {
        const status = window.editor?.cmd?.completionStatus?.(view.state);
        if (status === 'active' && window.editor?.cmd?.acceptCompletion) {
          window.editor.cmd.acceptCompletion(view);
        } else {
          view.dispatch(view.state.replaceSelection('\n'));
        }
      } else if (button === '{bksp}') {
        const { from, to } = view.state.selection.main;
        if (from !== to) {
          view.dispatch({ changes: { from, to }, userEvent: 'delete.backward' });
        } else if (from > 0) {
          view.dispatch({ changes: { from: from - 1, to: from }, userEvent: 'delete.backward' });
        }
      } else if (button === '{shift}' || button === '{shift2}') {
        _kbdShiftMode = !_kbdShiftMode;
        kbd.setOptions({ layoutName: _kbdShiftMode ? 'shift' : (_kbdCapsLock ? 'caps' : 'default') });
      } else if (button === '{caps}') {
        _kbdCapsLock = !_kbdCapsLock;
        if (!_kbdShiftMode) kbd.setOptions({ layoutName: _kbdCapsLock ? 'caps' : 'default' });
      } else if (button === '{space}') {
        view.dispatch({ ...view.state.replaceSelection(' '), userEvent: 'input.type' });
      } else if (button === '{undo}') {
        window.editor?.cmd?.undo?.(view);
      } else if (button === '{selectall}') {
        const { from, to, head } = view.state.selection.main;
        if (from === to) view.dispatch({ selection: { anchor: 0, head: view.state.doc.length } });
        else view.dispatch({ selection: { anchor: head } });
      } else if (button === '{cut}') {
        const { from, to } = view.state.selection.main;
        if (from !== to) {
          const text = view.state.sliceDoc(from, to);
          navigator.clipboard.writeText(text);
          view.dispatch({ changes: { from, to }, userEvent: 'delete.cut' });
        }
      } else if (button === '{copy}') {
        const { from, to } = view.state.selection.main;
        if (from !== to) navigator.clipboard.writeText(view.state.sliceDoc(from, to));
      } else if (button === '{paste}') {
        navigator.clipboard.readText().then(text => {
          if (text) { view.dispatch(view.state.replaceSelection(text)); view.focus(); }
        });
      } else if (button === '{left}') {
        window.editor?.cmd?.cursorCharLeft?.(view);
      } else if (button === '{right}') {
        window.editor?.cmd?.cursorCharRight?.(view);
      } else if (button === '{up}') {
        window.editor?.cmd?.cursorLineUp?.(view);
      } else if (button === '{down}') {
        window.editor?.cmd?.cursorLineDown?.(view);
      } else if (button === '{tab}') {
        window.editor?.cmd?.startCompletion?.(view);
      } else {
        view.dispatch({ ...view.state.replaceSelection(button), userEvent: 'input.type' });
      }
      view.focus();
    },
  });

  // Keep focus in editor when tapping keyboard keys, and stop propagation
  // so detached DOM nodes (from layout changes) don't trigger the dock close handler
  kbdEl.addEventListener('pointerdown', (e) => e.stopPropagation());
  kbdEl.addEventListener('mousedown', (e) => e.preventDefault());
  kbdEl.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });

  // --- Input dock toggle ---
  const inputDock = document.getElementById('input-dock');
  if (inputDock) {
    const editorCol = document.getElementById('editor-column');
    const editorMount = document.getElementById('editor-mount');

    const outputPanel = document.getElementById('output-panel');

    function updateSidebarPadding() {
      // No-op in the new bottom-drawer model: #bottom-layer is grid-placed inside
      // #main (drawer row below the sidebar column), so sidebar content shrinks to
      // make room — never overlaps the keyboard. Kept as a window export for
      // backward compat with inline script callers.
    }

    function isPortraitLayout() {
      return window.innerWidth <= 768 ||
        (window.matchMedia('(pointer: coarse)').matches && window.matchMedia('(orientation: portrait)').matches);
    }

    // Keep --kbd-h (the CSS custom prop driving editor/output-panel flex
    // sizing in portrait kbd-open) in sync with the actual keyboard dock
    // height. CSS handles the layout — nothing imperative to do here beyond
    // writing the measured height.
    function updateKbdHeightVar() {
      const h = inputDock.offsetHeight;
      if (h > 0) document.documentElement.style.setProperty('--kbd-h', h + 'px');
    }
    if (typeof ResizeObserver !== 'undefined') {
      // rAF defer to avoid "ResizeObserver loop" warning: writing the
      // --kbd-h CSS var can cascade into other element resizes that
      // re-fire RO synchronously. Next-frame defer breaks the cycle.
      new ResizeObserver(() => requestAnimationFrame(updateKbdHeightVar)).observe(inputDock);
    }
    // Retained as a no-op shim so existing call sites don't break. The old
    // implementation set explicit editor-column height imperatively, which
    // fought with flex layout and missed the real keyboard height whenever
    // simple-keyboard rendered asynchronously. The CSS-driven approach
    // replaces it.
    function ensureKeyboardVisible() {}

    function openDock() {
      if (window._kbdMode === 'system') return;
      const isTouch = window.matchMedia('(pointer: coarse)').matches || document.body.classList.contains('force-kbd');
      if (!isTouch) return;
      if (window.getBottom && window.getBottom() === 'kbd') return;
      // Opening the keyboard claims the bottom drawer — setBottom handles
      // dismissing any previous occupant (CTA, XY pad).
      if (window.setBottom) window.setBottom('kbd');
      inputDock.classList.add('open');
      requestAnimationFrame(() => {
        ensureKeyboardVisible();
        updateSidebarPadding();
      });
    }
    function closeDock() {
      if (!inputDock.classList.contains('open')) return;
      inputDock.classList.remove('open');
      if (window.setBottom && window.getBottom && window.getBottom() === 'kbd') {
        window.setBottom('none');
      }
      updateSidebarPadding();
    }
    window._updateSidebarPadding = updateSidebarPadding;
    window._closeKbdDock = closeDock;

    if (editorMount) {
      // Touch keyboard trigger: opens DURING the hold (not on release) the
      // moment the long-press threshold is reached. Catches the iOS
      // magnifier case where the user holds to position the cursor via
      // the magnifier — previously they had to release after the
      // threshold to get the keyboard, which was easy to miss-time. Now
      // the keyboard pops up while they're still touching.
      // Quick taps that release before the threshold don't open
      // (timer cancelled). On desktop any click opens.
      let tapStartX = 0, tapStartY = 0, tapPointerId = -1, tapStartTime = 0;
      let kbdOpenTimer = null;
      const TAP_THRESHOLD = 10;
      function clearKbdTimer() {
        if (kbdOpenTimer) { clearTimeout(kbdOpenTimer); kbdOpenTimer = null; }
      }
      editorMount.addEventListener('pointerdown', (e) => {
        tapStartX = e.clientX;
        tapStartY = e.clientY;
        tapPointerId = e.pointerId;
        tapStartTime = Date.now();
        // Schedule a during-hold keyboard open. The user's slider value
        // (window._kbdLongPressMs) sets when. Default 0 means "no delay,
        // open on touch immediately" — for that case we still want the
        // pointerup path to handle it (preserves desktop click behavior).
        clearKbdTimer();
        const delay = window._kbdLongPressMs || 0;
        if (window.matchMedia('(pointer: coarse)').matches && delay > 0) {
          const startedPointerId = e.pointerId;
          kbdOpenTimer = setTimeout(() => {
            kbdOpenTimer = null;
            // Still actively held? (not released, not moved out)
            if (tapPointerId !== startedPointerId) return;
            // If user has dragged a selection (text highlighted), don't pop
            // the keyboard — they're selecting, not editing.
            const v = window.editor?.view;
            if (v) {
              const { from, to } = v.state.selection.main;
              if (from !== to) return;
            }
            openDock();
          }, delay);
        }
      });
      editorMount.addEventListener('pointermove', (e) => {
        if (e.pointerId !== tapPointerId) return;
        if (Math.abs(e.clientX - tapStartX) > TAP_THRESHOLD ||
            Math.abs(e.clientY - tapStartY) > TAP_THRESHOLD) {
          tapPointerId = -1;
          clearKbdTimer();
        }
      });
      editorMount.addEventListener('pointerup', (e) => {
        if (e.pointerId !== tapPointerId) return;
        const held = Date.now() - tapStartTime;
        tapPointerId = -1;
        clearKbdTimer();
        // Touch: require long-press to avoid accidental keyboard. If the
        // hold reached the threshold the during-hold timer ABOVE already
        // opened the keyboard; this branch handles the desktop click and
        // the touch zero-delay (slider=0) case.
        if (window.matchMedia('(pointer: coarse)').matches && held < (window._kbdLongPressMs || 0)) return;
        setTimeout(() => {
          const v = window.editor?.view;
          if (v) {
            const { from, to } = v.state.selection.main;
            if (from !== to) return;
          }
          openDock();
        }, 0);
      });
      editorMount.addEventListener('pointercancel', () => {
        tapPointerId = -1;
        clearKbdTimer();
      });

      // iOS magnifier detection: when the user press-and-holds in the
      // editor and the iPhone magnifier appears, dragging it moves the
      // text cursor — that fires `selectionchange`. We catch the SECOND
      // selectionchange during a touch (the first is the initial tap-
      // placement; subsequent ones are magnifier-driven cursor moves)
      // and open the keyboard on that signal alone, regardless of how
      // long the user held. Quick taps fire only one selectionchange so
      // they don't trigger this — they go through the pointerup branch
      // governed by the slider threshold.
      let _touchStartTime = 0;
      let _selChangeCount = 0;
      let _kbdOpenedThisTouch = false;
      editorMount.addEventListener('pointerdown', (e) => {
        if (!window.matchMedia('(pointer: coarse)').matches) return;
        _touchStartTime = Date.now();
        _selChangeCount = 0;
        _kbdOpenedThisTouch = false;
      }, true);  // capture so we run before the existing handler's logic
      const _onSelectionChange = () => {
        if (!_touchStartTime || _kbdOpenedThisTouch) return;
        _selChangeCount++;
        // First selectionchange = initial tap placement. Second (or later)
        // = the magnifier dragged the cursor. Also require at least 80ms
        // of hold to filter out browser-internal double-fires on tap.
        if (_selChangeCount < 2) return;
        if (Date.now() - _touchStartTime < 80) return;
        _kbdOpenedThisTouch = true;
        openDock();
      };
      document.addEventListener('selectionchange', _onSelectionChange);
      editorMount.addEventListener('pointerup', () => { _touchStartTime = 0; }, true);
      editorMount.addEventListener('pointercancel', () => { _touchStartTime = 0; }, true);
    }
    // Outside-tap close: only on desktop (non-touch). On touch, keyboard stays open until {hide}.
    document.addEventListener('pointerdown', (e) => {
      if (!inputDock.classList.contains('open')) return;
      if (window.matchMedia('(pointer: coarse)').matches) return;
      if (inputDock.contains(e.target)) return;
      if (editorMount && editorMount.contains(e.target)) return;
      if (e.target.closest('#buffer-tabs, #panel-divider, #info-divider, #log-divider, #output-panel')) return;
      closeDock();
    });
  }

  // --- FX Bypass ---
  let fxBypassed = false;
  const fxBypassBtn = document.getElementById('fx-bypass');
  if (fxBypassBtn) {
    fxBypassBtn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      fxBypassed = !fxBypassed;
      fxBypassBtn.classList.toggle('active', fxBypassed);
      try {
        if (window._sonicSend) {
          const mix = fxBypassed ? 0 : 1;
          if (window._FX_HPF) window._sonicSend('/n_set', window._FX_HPF, 'mix', mix);
          if (window._FX_LPF) window._sonicSend('/n_set', window._FX_LPF, 'mix', mix);
        }
      } catch (_) {}
    });
  }

  // --- FX Close: × on the XY-pad hides it by setting bottom drawer to none ---
  const fxCloseBtn = document.getElementById('fx-close');
  if (fxCloseBtn) {
    fxCloseBtn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (window.setBottom) window.setBottom('none');
      try { localStorage.setItem('pane-fx', 'false'); } catch (_) {}
    });
  }

  // --- FX Pad ---
  const fxCanvas = document.getElementById('fx-pad');
  if (fxCanvas) {
    const fxCtx = fxCanvas.getContext('2d');
    let padActive = false;
    let padX = 0.5, padY = 0.5;
    let rafPending = false;

    function resizePad() {
      const rect = fxCanvas.getBoundingClientRect();
      fxCanvas.width = rect.width * devicePixelRatio;
      fxCanvas.height = rect.height * devicePixelRatio;
      fxCtx.scale(devicePixelRatio, devicePixelRatio);
      drawPad();
    }

    function drawPad() {
      const w = fxCanvas.clientWidth;
      const h = fxCanvas.clientHeight;
      const isLight = document.body.classList.contains('theme-light');
      const isHC = document.body.classList.contains('theme-highcontrast');
      fxCtx.clearRect(0, 0, w, h);
      // Background
      const bgColor = getComputedStyle(document.body).getPropertyValue('--bg-panel').trim() || '#0a0a0a';
      fxCtx.fillStyle = bgColor;
      fxCtx.fillRect(0, 0, w, h);
      // Grid
      fxCtx.strokeStyle = isHC ? 'rgba(255,255,0,0.15)' : isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)';
      fxCtx.lineWidth = 1;
      for (let i = 1; i < 10; i++) {
        fxCtx.beginPath(); fxCtx.moveTo(i * w / 10, 0); fxCtx.lineTo(i * w / 10, h); fxCtx.stroke();
        fxCtx.beginPath(); fxCtx.moveTo(0, i * h / 10); fxCtx.lineTo(w, i * h / 10); fxCtx.stroke();
      }
      // Crosshair
      const cx = padX * w, cy = (1 - padY) * h;
      const accent = isHC ? '#ffff00' : '#FF1493';
      fxCtx.strokeStyle = isHC ? 'rgba(255,255,0,0.6)' : 'rgba(255,20,147,0.7)';
      fxCtx.lineWidth = 1;
      fxCtx.beginPath(); fxCtx.moveTo(cx, 0); fxCtx.lineTo(cx, h); fxCtx.stroke();
      fxCtx.beginPath(); fxCtx.moveTo(0, cy); fxCtx.lineTo(w, cy); fxCtx.stroke();
      // Dot
      fxCtx.fillStyle = accent;
      fxCtx.shadowColor = accent;
      fxCtx.shadowBlur = 15;
      fxCtx.beginPath(); fxCtx.arc(cx, cy, 8, 0, Math.PI * 2); fxCtx.fill();
      fxCtx.shadowBlur = 0;
      // Dual circular FFT: frequency (pink) + waveform (cyan)
      if (typeof analyser !== 'undefined' && analyser) {
        const freqData = new Uint8Array(analyser.frequencyBinCount);
        const waveData = new Uint8Array(analyser.fftSize);
        analyser.getByteFrequencyData(freqData);
        analyser.getByteTimeDomainData(waveData);
        const ccx = w / 2, ccy = h / 2;
        const baseR = Math.min(w, h) * 0.08;
        const maxR = Math.min(w, h) * 0.38;
        const bins = Math.min(freqData.length, 180);
        const waveBins = Math.min(waveData.length, 180);
        // Waveform ring (inner, cyan/green) — drawn first so frequency overlays
        fxCtx.beginPath();
        for (let i = 0; i <= waveBins; i++) {
          const idx = i % waveBins;
          const angle = (idx / waveBins) * Math.PI * 2 - Math.PI / 2;
          const amp = (waveData[idx] - 128) / 128;
          const r = baseR * 1.5 + Math.abs(amp) * maxR * 0.6;
          const px = ccx + Math.cos(angle) * r;
          const py = ccy + Math.sin(angle) * r;
          if (i === 0) fxCtx.moveTo(px, py);
          else fxCtx.lineTo(px, py);
        }
        fxCtx.closePath();
        fxCtx.fillStyle = isHC ? 'rgba(0,255,128,0.15)' : isLight ? 'rgba(0,180,220,0.12)' : 'rgba(0,200,255,0.12)';
        fxCtx.strokeStyle = isHC ? 'rgba(0,255,128,0.7)' : isLight ? 'rgba(0,180,220,0.5)' : 'rgba(0,200,255,0.5)';
        fxCtx.lineWidth = 2;
        fxCtx.fill();
        fxCtx.stroke();
        // Frequency ring (outer, pink/yellow)
        fxCtx.beginPath();
        for (let i = 0; i <= bins; i++) {
          const idx = i % bins;
          const angle = (idx / bins) * Math.PI * 2 - Math.PI / 2;
          const amp = freqData[idx] / 255;
          const r = baseR + amp * maxR;
          const px = ccx + Math.cos(angle) * r;
          const py = ccy + Math.sin(angle) * r;
          if (i === 0) fxCtx.moveTo(px, py);
          else fxCtx.lineTo(px, py);
        }
        fxCtx.closePath();
        fxCtx.fillStyle = isHC ? 'rgba(255,255,0,0.15)' : 'rgba(255,20,147,0.12)';
        fxCtx.strokeStyle = isHC ? 'rgba(255,255,0,0.7)' : 'rgba(255,20,147,0.5)';
        fxCtx.lineWidth = 2;
        fxCtx.shadowColor = isHC ? '#0f0' : '#FF1493';
        fxCtx.shadowBlur = 8;
        fxCtx.fill();
        fxCtx.stroke();
        fxCtx.shadowBlur = 0;
      }
      // Labels
      fxCtx.fillStyle = isHC ? '#ffff00' : isLight ? '#888' : '#999';
      fxCtx.font = '10px monospace';
      fxCtx.fillText('brightness →', 4, h - 4);
      fxCtx.save();
      fxCtx.translate(12, h - 16);
      fxCtx.rotate(-Math.PI / 2);
      fxCtx.fillText('fullness →', 0, 0);
      fxCtx.restore();
    }

    function sendFx() {
      if (!window._sonicSend) return;
      const lpf = 10 + padX * 120;       // right = bright (10-130 MIDI)
      const hpf = 130 - padY * 120;      // top = full bass (130→10), bottom = thin (10→130)
      try {
        if (window._FX_LPF) window._sonicSend('/n_set', window._FX_LPF, 'cutoff', lpf);
        if (window._FX_HPF) window._sonicSend('/n_set', window._FX_HPF, 'cutoff', Math.min(hpf, lpf - 5));
      } catch (_) {}
    }

    function updatePad(e) {
      const rect = fxCanvas.getBoundingClientRect();
      padX = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      padY = Math.max(0, Math.min(1, 1 - (e.clientY - rect.top) / rect.height));
      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(() => { rafPending = false; drawPad(); sendFx(); });
      }
    }

    fxCanvas.addEventListener('pointerdown', (e) => { padActive = true; fxCanvas.setPointerCapture(e.pointerId); updatePad(e); });
    fxCanvas.addEventListener('pointermove', (e) => { if (padActive) updatePad(e); });
    fxCanvas.addEventListener('pointerup', () => { padActive = false; });
    fxCanvas.addEventListener('pointercancel', () => { padActive = false; });

    // rAF defer to avoid "ResizeObserver loop" warning when canvas
    // resize triggers further layout shifts in the FX panel.
    new ResizeObserver(() => requestAnimationFrame(resizePad)).observe(fxCanvas);
    resizePad();

    // Continuous animation for FFT circular viz
    function animatePad() {
      if (fxCanvas.offsetParent !== null) drawPad();
      requestAnimationFrame(animatePad);
    }
    animatePad();
  }
}
